<template>
    <div class="mechanic-asset-card">
        <div v-if="loading" class="text-center text-muted py-4">
            Загрузка
        </div>

        <div v-else-if="unit">
            <div class="mechanic-asset-header">
                <div class="mechanic-asset-photo-box">
                    <button
                            v-if="mainPhoto"
                            type="button"
                            class="mechanic-asset-primary-photo"
                            title="Открыть просмотр фото"
                            @click="openPhotoViewer(mainPhoto)"
                    >
                        <img
                                :src="previewFileUrl(mainPhoto, 420, 280)"
                                :alt="mainPhoto.file_name"
                                loading="lazy"
                                decoding="async"
                        />
                        <span class="mechanic-asset-primary-photo-badge">
                            <i class="fas fa-star mr-1"></i>
                            {{ mainPhoto.is_primary_photo ? 'Заглавное' : 'Фото' }}
                        </span>
                        <span
                                v-if="photoRows.length > 1"
                                class="mechanic-asset-photo-count"
                        >
                            {{ mainPhotoIndex + 1 }} / {{ photoRows.length }}
                        </span>
                    </button>
                    <div v-else class="mechanic-asset-primary-photo mechanic-asset-primary-photo--empty">
                        <i class="fas fa-camera"></i>
                        <span>Фото не выбрано</span>
                    </div>
                    <div
                            v-if="photoRows.length > 1"
                            class="mechanic-asset-photo-strip"
                    >
                        <button
                                v-for="file in photoRows"
                                :key="file.id"
                                type="button"
                                class="mechanic-asset-photo-thumb"
                                :class="{'mechanic-asset-photo-thumb--active': mainPhoto && Number(mainPhoto.id) === Number(file.id)}"
                                :title="fileDisplayName(file)"
                                @click="selectHeaderPhoto(file)"
                        >
                            <img
                                    :src="previewFileUrl(file, 96, 72)"
                                    :alt="file.file_name"
                                    loading="lazy"
                                    decoding="async"
                            />
                            <i v-if="isMainPhoto(file)" class="fas fa-star"></i>
                        </button>
                    </div>
                    <b-button
                            v-if="canWrite"
                            size="sm"
                            :variant="mainPhoto ? 'light' : 'outline-primary'"
                            class="mechanic-asset-photo-action"
                            @click="openPrimaryPhotoModal"
                    >
                        <i class="fas fa-camera mr-1"></i>
                        {{ mainPhoto ? 'Сменить фото' : 'Задать фото' }}
                    </b-button>
                </div>

                <div class="mechanic-asset-summary">
                    <div>
                        <span class="text-muted">Статус</span>
                        <b-badge
                                :variant="statusVariant(unit.status)"
                                class="mechanic-asset-status-badge"
                        >
                            {{ unit.status_label || unit.status }}
                        </b-badge>
                    </div>
                    <div>
                        <span class="text-muted">Местонахождение</span>
                        <strong>{{ unit.location_type_label || unit.location_type || '-' }}</strong>
                        <small class="d-block text-muted">{{ locationName(unit) || '-' }}</small>
                    </div>
                    <div>
                        <span class="text-muted">МОЛ</span>
                        <strong>
                            <EmployeeLink
                                    :user-id="unit.responsible_user_id"
                                    :fallback="unit.responsible_name || '-'"
                            />
                        </strong>
                    </div>
                    <div>
                        <span class="text-muted">Компания владелец</span>
                        <strong>{{ ownerCompanyText(unit) }}</strong>
                    </div>
                </div>
            </div>

            <b-alert
                    v-if="activeTransfer"
                    show
                    variant="info"
                    class="mechanic-asset-transfer-alert"
            >
                <div class="mechanic-asset-transfer-title">
                    <b-badge variant="info" class="mechanic-asset-transfer-badge">Перемещается</b-badge>
                    <router-link
                            v-if="transferRequestId(activeTransfer)"
                            :to="transferRequestTo(activeTransfer)"
                            class="mechanic-asset-transfer-request"
                    >
                        Основание: #{{ transferRequestNumber(activeTransfer) }}
                    </router-link>
                </div>
                <div class="mechanic-asset-transfer-route">
                    <div class="mechanic-asset-transfer-point">
                        <span>Откуда</span>
                        <strong :title="transferFromText(activeTransfer)">{{ transferFromText(activeTransfer) }}</strong>
                    </div>
                    <div class="mechanic-asset-transfer-point">
                        <span>Куда</span>
                        <strong :title="transferToText(activeTransfer)">{{ transferToText(activeTransfer) }}</strong>
                    </div>
                </div>
            </b-alert>

            <b-alert
                    v-if="errorMessage"
                    variant="danger"
                    show
                    dismissible
                    @dismissed="errorMessage = null"
            >
                {{ errorMessage }}
            </b-alert>

            <b-alert v-if="unit.location_type === 'object_group'" variant="info" show>
                На группе — без конкретного объекта. Для операций по заявке или аренды назначьте технику на объект или склад через редактирование позиции.
            </b-alert>
            <div v-if="canWrite || canDelete" class="mechanic-asset-actions">
                <b-button v-if="canWrite" size="sm" variant="outline-success" @click="openEditUnitModal">
                    <i class="fas fa-pen mr-1"></i>
                    Редактировать
                </b-button>
                <b-button v-if="canWrite" size="sm" variant="outline-primary" @click="openNoteModal">
                    <i class="fas fa-comment-medical mr-1"></i>
                    Добавить примечание
                </b-button>
                <b-button
                        v-if="canWrite && activeRental && !unitIsQuantitative"
                        size="sm"
                        variant="outline-warning"
                        @click="openReturnRentalModal"
                >
                    <i class="fas fa-undo mr-1"></i>
                    Возврат из аренды
                </b-button>
                <b-button
                        v-else-if="canWrite && !unitIsQuantitative && unit.location_type !== 'object_group'"
                        size="sm"
                        variant="outline-primary"
                        @click="openRentModal"
                >
                    <i class="fas fa-handshake mr-1"></i>
                    Сдать в аренду
                </b-button>
                <b-button v-if="canDelete" size="sm" variant="outline-danger" @click="openArchiveModal">
                    <i class="fas fa-archive mr-1"></i>
                    В архив
                </b-button>
            </div>

            <b-tabs small v-model="activeTabIndex" class="mechanic-asset-tabs">
                <b-tab title="Карточка">
                    <dl class="mechanic-asset-details">
                        <dt>Группа техники</dt>
                        <dd>{{ unit.catalog_name || '-' }}</dd>
                        <dt>Категория</dt>
                        <dd>{{ unit.catalog_category_label || catalogCategoryText(unit.catalog_category) }}</dd>
                        <dt>Тип учета</dt>
                        <dd>{{ accountingTypeText(unit.accounting_type) }}</dd>
                        <dt v-if="unitIsQuantitative">Количество / остаток</dt>
                        <dd v-if="unitIsQuantitative">{{ quantityText(unit) }}</dd>
                        <dt v-if="!unitIsQuantitative">Инвентарный номер</dt>
                        <dd v-if="!unitIsQuantitative">{{ unit.inventory_number || '-' }}</dd>
                        <dt v-if="!unitIsQuantitative">QR</dt>
                        <dd v-if="!unitIsQuantitative">{{ unit.qr_code || '-' }}</dd>
                        <dt v-if="!unitIsQuantitative">VIN / серийный номер</dt>
                        <dd v-if="!unitIsQuantitative">{{ [unit.vin, unit.serial_number].filter(Boolean).join(' / ') || '-' }}</dd>
                        <dt v-if="!unitIsQuantitative">Госномер</dt>
                        <dd v-if="!unitIsQuantitative">{{ unit.plate_number || '-' }}</dd>
                        <dt>Компания владелец</dt>
                        <dd>{{ ownerCompanyText(unit) }}</dd>
                        <dt v-if="!unitIsQuantitative">Счетчик</dt>
                        <dd v-if="!unitIsQuantitative">{{ meterText(unit.current_meter_type, unit.current_meter_value) }}</dd>
                        <dt>Состояние</dt>
                        <dd>{{ unit.technical_condition || '-' }}</dd>
                        <dt>Импорт</dt>
                        <dd>{{ importSourceText(unit) }}</dd>
                        <dt>Внешние идентификаторы</dt>
                        <dd>
                            <div v-if="externalIds.length">
                                <div v-for="externalId in externalIds" :key="externalId.id || externalId.raw_value">
                                    {{ externalId.source }} / {{ externalId.identifier_type }}: {{ externalId.raw_value }}
                                </div>
                            </div>
                            <span v-else>-</span>
                        </dd>
                        <dt>Комментарий</dt>
                        <dd>{{ unit.comment || '-' }}</dd>
                    </dl>
                </b-tab>

                <b-tab v-if="!unitIsQuantitative" title="Нормы топлива">
                    <div class="mechanic-asset-tab-toolbar">
                        <b-button
                                v-if="canWrite"
                                size="sm"
                                variant="outline-success"
                                @click="openFuelNormModal()"
                        >
                            <i class="fas fa-plus mr-1"></i>
                            Добавить норму
                        </b-button>
                    </div>
                    <b-table
                            :items="fuelNormRows"
                            :fields="fuelNormFields"
                            small
                            responsive="sm"
                            show-empty
                            empty-text="Нет норм"
                    >
                        <template #cell(norm_type)="data">
                            {{ data.item.norm_type_label || data.item.norm_type }}
                        </template>
                        <template #cell(value)="data">
                            {{ data.item.value }} {{ data.item.value_unit }}
                        </template>
                        <template #cell(period)="data">
                            {{ formatDate(data.item.effective_from) || '...' }} - {{ formatDate(data.item.effective_to) || '...' }}
                        </template>
                        <template #cell(actions)="data">
                            <b-button
                                    :href="fileUrl(data.item)"
                                    :download="fileDisplayName(data.item)"
                                    target="_blank"
                                    size="sm"
                                    variant="outline-primary"
                                    title="Скачать файл"
                                    class="mr-1"
                            >
                                <i class="fas fa-download"></i>
                            </b-button>
                            <b-button
                                    v-if="canWrite"
                                    size="sm"
                                    variant="outline-success"
                                    class="mr-1"
                                    title="Редактировать"
                                    @click="openFuelNormModal(data.item)"
                            >
                                <i class="fas fa-pen"></i>
                            </b-button>
                            <b-button
                                    v-if="canWrite"
                                    size="sm"
                                    variant="outline-danger"
                                    title="Удалить"
                                    @click="openDeleteFuelNormModal(data.item)"
                            >
                                <i class="fas fa-times"></i>
                            </b-button>
                        </template>
                    </b-table>
                </b-tab>

                <b-tab v-if="!unitIsQuantitative" title="Показания">
                    <div class="mechanic-asset-tab-toolbar">
                        <b-button
                                v-if="canWrite"
                                size="sm"
                                variant="outline-success"
                                @click="openMeterModal"
                        >
                            <i class="fas fa-tachometer-alt mr-1"></i>
                            Добавить показание
                        </b-button>
                    </div>
                    <div class="mechanic-asset-current-meter">
                        <span class="text-muted">Текущее показание</span>
                        <strong>{{ meterText(unit.current_meter_type, unit.current_meter_value) }}</strong>
                    </div>
                    <div class="text-muted small">
                        Полная лента изменений показаний фиксируется в истории карточки.
                    </div>
                </b-tab>

                <b-tab v-if="!unitIsQuantitative" title="Аренда">
                    <div v-if="activeRental" class="mechanic-asset-rental">
                        <dl class="mechanic-asset-details">
                            <dt>Арендатор</dt>
                            <dd>{{ activeRental.renter_name || '-' }}</dd>
                            <dt>Оператор</dt>
                            <dd>
                                <EmployeeLink
                                        :user="activeRental.Operator"
                                        :user-id="activeRental.operator_user_id"
                                        :fallback="rentalUserName(activeRental.Operator, activeRental.operator_user_id)"
                                />
                            </dd>
                            <dt>Ответственный</dt>
                            <dd>
                                <EmployeeLink
                                        :user="activeRental.Responsible"
                                        :user-id="activeRental.responsible_user_id"
                                        :fallback="rentalUserName(activeRental.Responsible, activeRental.responsible_user_id)"
                                />
                            </dd>
                            <dt>Начало</dt>
                            <dd>{{ formatDateTime(activeRental.started_at) || '-' }}</dd>
                            <dt>Плановый возврат</dt>
                            <dd>{{ formatDate(activeRental.planned_return_at) || '-' }}</dd>
                            <dt>Место использования</dt>
                            <dd>{{ rentalLocation(activeRental) }}</dd>
                            <dt>Счетчик при передаче</dt>
                            <dd>{{ meterText(activeRental.transfer_meter_type, activeRental.transfer_meter_value) }}</dd>
                            <dt>Комментарий</dt>
                            <dd>{{ activeRental.comment || '-' }}</dd>
                        </dl>
                    </div>
                    <b-alert v-else show variant="secondary" class="mb-2">
                        Активной аренды нет.
                    </b-alert>

                    <b-table
                            :items="rentalRows"
                            :fields="rentalFields"
                            small
                            responsive="sm"
                            show-empty
                            empty-text="История аренды пуста"
                    >
                        <template #cell(period)="data">
                            {{ formatDate(data.item.started_at) || '...' }} - {{ formatDate(data.item.returned_at) || formatDate(data.item.planned_return_at) || '...' }}
                        </template>
                        <template #cell(status)="data">
                            {{ rentalStatusLabel(data.item.status) }}
                        </template>
                    </b-table>
                </b-tab>

                <b-tab title="История">
                    <b-alert v-if="!canReadHistory" show variant="secondary" class="mb-0">
                        История изменений доступна администраторам и пользователям с правом редактирования.
                    </b-alert>
                    <template v-else>
                        <div class="mechanic-history-view-toolbar">
                            <b-button-group size="sm" aria-label="Представление истории">
                                <b-button
                                        :variant="historyViewMode === 'timeline' ? 'primary' : 'outline-secondary'"
                                        @click="historyViewMode = 'timeline'"
                                >
                                    <i class="fas fa-stream mr-1"></i>Лента
                                </b-button>
                                <b-button
                                        :variant="historyViewMode === 'table' ? 'primary' : 'outline-secondary'"
                                        @click="historyViewMode = 'table'"
                                >
                                    <i class="fas fa-table mr-1"></i>Таблица
                                </b-button>
                            </b-button-group>
                            <small>{{ visibleHistoryRows.length }} записей</small>
                        </div>
                        <MechanicAssetHistoryFeed
                                v-if="historyViewMode === 'timeline'"
                                :events="readableHistoryEvents"
                        />
                        <div v-else class="mechanic-history-mobile d-md-none">
                            <b-alert v-if="!visibleHistoryRows.length" show variant="light" class="mb-0">
                                История пуста
                            </b-alert>
                            <article
                                    v-for="(row, index) in visibleHistoryRows"
                                    :key="row.id || index"
                                    class="mechanic-history-card"
                            >
                                <header class="mechanic-history-card__header">
                                    <div>
                                        <strong>{{ historyActionLabel(row.action) }}</strong>
                                        <small>{{ formatDateTime(row.created_at) || '-' }}</small>
                                    </div>
                                    <router-link
                                            v-if="row.request_id"
                                            :to="`/requests/${row.request_id}`"
                                            class="mechanic-history-card__request"
                                    >
                                        #{{ row.request_number || row.request_id }}
                                    </router-link>
                                </header>
                                <dl class="mechanic-history-card__meta">
                                    <div>
                                        <dt>Кто изменил</dt>
                                        <dd>{{ row.changed_by_name || '-' }}</dd>
                                    </div>
                                    <div v-if="row.comment">
                                        <dt>Комментарий</dt>
                                        <dd>{{ row.comment }}</dd>
                                    </div>
                                </dl>
                                <div class="mechanic-history-card__changes">
                                    <span
                                            v-for="(line, lineIndex) in formatHistoryChangeLines(row)"
                                            :key="lineIndex"
                                            class="mechanic-history-line"
                                    >
                                        {{ line }}
                                    </span>
                                    <span
                                            v-if="!formatHistoryChangeLines(row).length"
                                            class="text-muted"
                                    >
                                        Изменения не указаны
                                    </span>
                                </div>
                            </article>
                        </div>
                        <div v-if="historyViewMode === 'table'" class="d-none d-md-block">
                            <b-table
                                    :items="visibleHistoryRows"
                                    :fields="historyFields"
                                    class="mechanic-history-table"
                                    small
                                    responsive
                                    show-empty
                                    empty-text="История пуста"
                            >
                                <template #cell(created_at)="data">
                                    {{ formatDateTime(data.item.created_at) }}
                                </template>
                                <template #cell(action)="data">
                                    {{ historyActionLabel(data.item.action) }}
                                </template>
                                <template #cell(request)="data">
                                    <router-link
                                            v-if="data.item.request_id"
                                            :to="`/requests/${data.item.request_id}`"
                                    >
                                        #{{ data.item.request_number || data.item.request_id }}
                                    </router-link>
                                    <span v-else class="text-muted">-</span>
                                </template>
                                <template #cell(changes)="data">
                                    <small class="mechanic-history-changes">
                                        <span
                                                v-for="(line, index) in formatHistoryChangeLines(data.item)"
                                                :key="index"
                                                class="mechanic-history-line"
                                        >
                                            {{ line }}
                                        </span>
                                        <span
                                                v-if="!formatHistoryChangeLines(data.item).length"
                                                class="text-muted"
                                        >
                                            -
                                        </span>
                                    </small>
                                </template>
                            </b-table>
                        </div>
                    </template>
                </b-tab>

                <b-tab title="Примечания">
                    <div class="mechanic-asset-tab-toolbar">
                        <b-button
                                v-if="canWrite"
                                size="sm"
                                variant="outline-success"
                                @click="openNoteModal"
                        >
                            <i class="fas fa-plus mr-1"></i>
                            Добавить примечание
                        </b-button>
                    </div>
                    <div ref="notesPanel" class="mechanic-asset-notes">
                        <b-alert v-if="!noteList.length" show variant="secondary" class="mb-0">
                            Нет примечаний.
                        </b-alert>
                        <article
                                v-for="note in noteList"
                                :key="note.id"
                                class="mechanic-asset-note-card"
                        >
                            <div class="mechanic-asset-note-head">
                                <div class="mechanic-asset-note-meta">
                                    <b-badge variant="light" class="mechanic-asset-note-type">
                                        {{ noteTypeText(note.note_type) }}
                                    </b-badge>
                                    <strong>{{ note.author_name || '-' }}</strong>
                                </div>
                                <small class="text-muted">{{ formatDateTime(note.created_at) }}</small>
                            </div>
                            <div class="mechanic-asset-note-text">
                                {{ note.text }}
                            </div>
                            <div
                                    v-if="noteFiles(note).length"
                                    class="mechanic-note-file-grid"
                            >
                                <a
                                        v-for="file in noteFiles(note)"
                                        :key="file.id"
                                        :href="fileUrl(file)"
                                        target="_blank"
                                        :class="[
                                            'mechanic-note-file-preview',
                                            {'mechanic-note-file-preview--image': isImageFile(file)}
                                        ]"
                                        @click="handleFilePreviewClick($event, file)"
                                >
                                    <img
                                            v-if="isImageFile(file)"
                                            :src="previewFileUrl(file, 220, 160)"
                                            :alt="fileDisplayName(file)"
                                            loading="lazy"
                                            decoding="async"
                                    />
                                    <span v-else class="mechanic-note-file-icon">
                                        <i :class="fileIconClass(file)"></i>
                                        <small>{{ fileFormatLabel(file) }}</small>
                                    </span>
                                    <span class="mechanic-note-file-name">
                                        {{ fileDisplayName(file) }}
                                    </span>
                                </a>
                            </div>
                        </article>
                    </div>
                </b-tab>

                <b-tab title="Файлы">
                    <div v-if="canWrite" class="mechanic-asset-file-upload-block">
                        <MechanicAssetFileUploadPanel
                                v-model="fileForm.files"
                                :disabled="isSubmitting"
                        />
                        <b-alert
                                v-if="fileUpload.active"
                                show
                                variant="info"
                                class="mechanic-note-upload-status mt-2 mb-0"
                        >
                            <div class="mechanic-note-upload-title">
                                <b-spinner small class="mr-2"></b-spinner>
                                <span>{{ fileUploadText }}</span>
                            </div>
                            <b-progress
                                    :value="fileUploadPercent"
                                    max="100"
                                    height="8px"
                                    animated
                                    striped
                                    class="mt-2"
                            ></b-progress>
                        </b-alert>
                        <b-button
                                size="sm"
                                variant="outline-success"
                                class="mt-2"
                                :disabled="isSubmitting || !fileForm.files || !fileForm.files.length"
                                @click="submitFileForm"
                        >
                            <b-spinner v-if="fileUpload.active" small class="mr-1"></b-spinner>
                            <i v-else class="fas fa-upload mr-1"></i>
                            {{ fileUpload.active ? 'Загружаю файлы' : 'Загрузить' }}
                        </b-button>
                    </div>
                    <b-table
                            :items="fileRows"
                            :fields="fileTableFields"
                            small
                            responsive="sm"
                            show-empty
                            empty-text="Файлы не прикреплены"
                    >
                        <template #cell(file_name)="data">
                            <a
                                    :href="fileUrl(data.item)"
                                    target="_blank"
                                    class="mechanic-file-table-link"
                                    @click="handleFilePreviewClick($event, data.item)"
                            >
                                <span
                                        :class="[
                                            'mechanic-file-table-preview',
                                            {'mechanic-file-table-preview--image': isImageFile(data.item)}
                                        ]"
                                >
                                    <img
                                            v-if="isImageFile(data.item)"
                                            :src="previewFileUrl(data.item, 96, 72)"
                                            :alt="fileDisplayName(data.item)"
                                            loading="lazy"
                                            decoding="async"
                                    />
                                    <span v-else class="mechanic-file-table-format">
                                        <i :class="fileIconClass(data.item)"></i>
                                        <small>{{ fileFormatLabel(data.item) }}</small>
                                    </span>
                                </span>
                                <span>{{ fileDisplayName(data.item) }}</span>
                            </a>
                        </template>
                        <template #cell(created_at)="data">
                            {{ formatDateTime(data.item.created_at) }}
                        </template>
                        <template #cell(primary)="data">
                            <b-badge v-if="data.item.is_primary_photo" variant="warning">
                                <i class="fas fa-star mr-1"></i>
                                Заглавное
                            </b-badge>
                            <span v-else class="text-muted">-</span>
                        </template>
                        <template #cell(actions)="data">
                            <b-button
                                    v-if="canWrite"
                                    size="sm"
                                    variant="outline-danger"
                                    title="Удалить файл"
                                    :disabled="isSubmitting"
                                    @click="deleteFile(data.item)"
                            >
                                <i class="fas fa-trash"></i>
                            </b-button>
                        </template>
                    </b-table>
                </b-tab>
            </b-tabs>
        </div>

        <b-modal
                v-model="showEditUnitModal"
                title="Редактирование карточки"
                ok-title="Сохранить"
                cancel-title="Отмена"
                :busy="isSubmitting"
                :ok-disabled="!isUnitFormValid"
                @ok.prevent="submitUnitForm"
        >
            <b-form @submit.stop.prevent="submitUnitForm">
                <b-form-group label="Тип позиции">
                    <b-form-radio-group
                            v-model="unitForm.accounting_type"
                            :options="accountingTypeOptions"
                            buttons
                            button-variant="outline-primary"
                            size="sm"
                    ></b-form-radio-group>
                </b-form-group>
                <b-form-group label="Группа техники / тип">
                    <v-select
                            v-model="unitForm.catalog_id"
                            :options="catalogOptions"
                            :reduce="option => option.value"
                            label="text"
                            placeholder="Выберите группу техники"
                    ></v-select>
                </b-form-group>
                <b-form-group label="Отображаемое имя">
                    <b-form-input v-model.trim="unitForm.display_name"></b-form-input>
                </b-form-group>
                <b-form-group label="Статус">
                    <b-form-select v-model="unitForm.status" :options="statusOptions"></b-form-select>
                </b-form-group>
                <b-form-group label="Местонахождение">
                    <b-form-select v-model="unitForm.location_type" :options="locationTypeOptions"></b-form-select>
                </b-form-group>
                <b-form-group v-if="unitUsesWarehouse" label="Склад / ремзона">
                    <v-select
                            v-model="unitForm.warehouse_id"
                            :options="warehouseOptions"
                            :reduce="option => option.value"
                            label="text"
                            placeholder="Не указан"
                    ></v-select>
                </b-form-group>
                <b-form-group v-if="unitUsesObject" label="Объект">
                    <v-select
                            v-model="unitForm.building_process_id"
                            :options="objectOptions"
                            :reduce="option => option.value"
                            label="text"
                            placeholder="Не указан"
                    ></v-select>
                </b-form-group>
                <b-form-group v-if="unitForm.location_type === 'object_group'" label="Группа объектов" description="Без назначения на конкретный объект">
                    <v-select
                            v-model="unitForm.object_group_id"
                            :options="assignableObjectGroupOptions"
                            :reduce="option => option.value"
                            label="text"
                            placeholder="Выберите группу объектов"
                    ></v-select>
                </b-form-group>
                <b-form-group label="МОЛ">
                    <v-select
                            v-model="unitForm.responsible_user_id"
                            :options="userOptions"
                            :reduce="option => option.value"
                            label="text"
                            placeholder="Не указан"
                    ></v-select>
                </b-form-group>
                <b-row v-if="unitFormIsQuantitative">
                    <b-col md="6">
                        <b-form-group label="Количество / остаток">
                            <b-form-input
                                    v-model.number="unitForm.quantity"
                                    type="number"
                                    min="0"
                                    step="0.001"
                            ></b-form-input>
                        </b-form-group>
                    </b-col>
                    <b-col md="6">
                        <b-form-group label="Единица измерения">
                            <b-form-input v-model.trim="unitForm.unit"></b-form-input>
                        </b-form-group>
                    </b-col>
                </b-row>
                <b-form-group label="Состояние">
                    <b-form-input v-model.trim="unitForm.technical_condition"></b-form-input>
                </b-form-group>
                <b-form-group v-if="!unitFormIsQuantitative" label="Инвентарный номер">
                    <b-form-input v-model.trim="unitForm.inventory_number"></b-form-input>
                </b-form-group>
                <b-form-group v-if="!unitFormIsQuantitative" label="Госномер">
                    <b-form-input v-model.trim="unitForm.plate_number"></b-form-input>
                </b-form-group>
                <b-form-group v-if="!unitFormIsQuantitative" label="VIN">
                    <b-form-input v-model.trim="unitForm.vin"></b-form-input>
                </b-form-group>
                <b-form-group v-if="!unitFormIsQuantitative" label="Серийный номер">
                    <b-form-input v-model.trim="unitForm.serial_number"></b-form-input>
                </b-form-group>
                <b-form-group v-if="!unitFormIsQuantitative" label="Тип счетчика">
                    <b-form-select v-model="unitForm.current_meter_type" :options="meterTypeOptions"></b-form-select>
                </b-form-group>
                <b-form-group v-if="!unitFormIsQuantitative" label="Текущее показание">
                    <b-form-input
                            v-model.number="unitForm.current_meter_value"
                            type="number"
                            min="0"
                            step="0.001"
                    ></b-form-input>
                </b-form-group>
                <b-form-group label="Компания владелец">
                    <b-form-input v-model.trim="unitForm.accounting_company_name"></b-form-input>
                </b-form-group>
                <b-form-group label="Комментарий">
                    <AppTextarea v-model.trim="unitForm.comment"></AppTextarea>
                </b-form-group>
                <b-form-group label="Комментарий к истории">
                    <AppTextarea v-model.trim="unitForm.history_comment"></AppTextarea>
                </b-form-group>
            </b-form>
        </b-modal>

        <b-modal
                v-model="showNoteModal"
                title="Новое примечание"
                size="lg"
                body-class="mechanic-note-modal-body"
                :ok-title="noteModalOkTitle"
                cancel-title="Отмена"
                :busy="isSubmitting"
                :ok-disabled="isNoteSubmitDisabled"
                @hidden="resetNoteForm"
                @ok.prevent="submitNoteForm"
        >
            <b-form @submit.stop.prevent="submitNoteForm">
                <b-alert
                        v-if="errorMessage"
                        show
                        variant="danger"
                        class="mb-3"
                >
                    {{ errorMessage }}
                </b-alert>
                <b-alert
                        v-if="noteForm.created_note_id && noteForm.files && noteForm.files.length"
                        show
                        variant="warning"
                        class="mb-3"
                >
                    Примечание уже добавлено. Повторная попытка загрузит только оставшиеся файлы.
                </b-alert>
                <b-alert
                        v-if="noteUpload.active"
                        show
                        variant="info"
                        class="mechanic-note-upload-status"
                >
                    <div class="mechanic-note-upload-title">
                        <b-spinner small class="mr-2"></b-spinner>
                        <span>{{ noteUploadText }}</span>
                    </div>
                    <b-progress
                            :value="noteUploadPercent"
                            max="100"
                            height="8px"
                            animated
                            striped
                            class="mt-2"
                    ></b-progress>
                </b-alert>
                <b-form-group label="Тип">
                    <b-form-select
                            v-model="noteForm.note_type"
                            :options="noteTypeOptions"
                            :disabled="isSubmitting || Boolean(noteForm.created_note_id)"
                    ></b-form-select>
                </b-form-group>
                <b-form-group label="Текст">
                    <AppTextarea
                            v-model.trim="noteForm.text"
                            required
                            :disabled="isSubmitting || Boolean(noteForm.created_note_id)"
                    ></AppTextarea>
                </b-form-group>
                <b-form-group label="Файлы">
                    <MechanicAssetFileUploadPanel
                            v-model="noteForm.files"
                            :disabled="isSubmitting"
                    />
                </b-form-group>
            </b-form>
        </b-modal>

        <b-modal
                v-model="showFuelNormModal"
                :title="fuelNormForm.id ? 'Редактирование нормы топлива' : 'Новая норма топлива'"
                ok-title="Сохранить"
                cancel-title="Отмена"
                :busy="isSubmitting"
                :ok-disabled="isSubmitting || !isFuelNormValid"
                @ok.prevent="submitFuelNormForm"
        >
            <b-form @submit.stop.prevent="submitFuelNormForm">
                <b-form-group label="Вид топлива">
                    <v-select
                            v-model="fuelNormForm.fuel_type_id"
                            :options="fuelTypeOptions"
                            :reduce="option => option.value"
                            label="text"
                            placeholder="Выберите топливо"
                    ></v-select>
                </b-form-group>
                <b-form-group label="Тип нормы">
                    <b-form-select
                            v-model="fuelNormForm.norm_type"
                            :options="fuelNormTypeOptions"
                    ></b-form-select>
                </b-form-group>
                <b-form-group label="Значение">
                    <b-form-input
                            v-model.number="fuelNormForm.value"
                            type="number"
                            min="0"
                            step="0.001"
                    ></b-form-input>
                </b-form-group>
                <b-form-group label="Единица значения">
                    <b-form-input v-model.trim="fuelNormForm.value_unit"></b-form-input>
                </b-form-group>
                <b-form-group v-if="fuelNormUsesWorkGroup" label="Группа работ">
                    <v-select
                            v-model="fuelNormForm.work_type_id"
                            :options="workTypeOptions"
                            :reduce="option => option.value"
                            label="text"
                            placeholder="Не указана"
                    ></v-select>
                </b-form-group>
                <b-form-group v-if="fuelNormUsesWorkGroup" label="Название группы работ вручную">
                    <b-form-input v-model.trim="fuelNormForm.work_type_name"></b-form-input>
                </b-form-group>
                <b-form-group v-if="fuelNormUsesWorkGroup" label="Единица работы">
                    <b-form-input v-model.trim="fuelNormForm.work_unit"></b-form-input>
                </b-form-group>
                <b-form-group label="Действует с">
                    <b-form-input v-model="fuelNormForm.effective_from" type="date"></b-form-input>
                </b-form-group>
                <b-form-group label="Действует по">
                    <b-form-input v-model="fuelNormForm.effective_to" type="date"></b-form-input>
                </b-form-group>
                <b-form-group label="Основание">
                    <b-form-input v-model.trim="fuelNormForm.basis_document"></b-form-input>
                </b-form-group>
                <b-form-checkbox v-model="fuelNormForm.is_approved" switch class="mb-3">
                    Утверждена
                </b-form-checkbox>
                <b-form-group label="Комментарий">
                    <AppTextarea v-model.trim="fuelNormForm.comment"></AppTextarea>
                </b-form-group>
            </b-form>
        </b-modal>

        <b-modal
                v-model="showDeleteFuelNormModal"
                title="Удаление нормы топлива"
                ok-title="Удалить"
                ok-variant="danger"
                cancel-title="Отмена"
                :busy="isSubmitting"
                @ok.prevent="deleteFuelNorm"
        >
            <p class="mb-0">Удалить норму расхода топлива?</p>
        </b-modal>

        <b-modal
                v-model="showMeterModal"
                title="Новое показание счетчика"
                ok-title="Записать"
                cancel-title="Отмена"
                :busy="isSubmitting"
                :ok-disabled="isSubmitting || !isMeterValid"
                @ok.prevent="submitMeterForm"
        >
            <b-form @submit.stop.prevent="submitMeterForm">
                <b-form-group label="Тип счетчика">
                    <b-form-select v-model="meterForm.meter_type" :options="meterTypeOptions"></b-form-select>
                </b-form-group>
                <b-form-group label="Показание">
                    <b-form-input
                            v-model.number="meterForm.value"
                            type="number"
                            min="0"
                            step="0.001"
                    ></b-form-input>
                </b-form-group>
                <b-form-group label="Дата фиксации">
                    <b-form-input v-model="meterForm.fixed_at" type="datetime-local"></b-form-input>
                </b-form-group>
                <b-form-group label="Комментарий">
                    <AppTextarea v-model.trim="meterForm.comment"></AppTextarea>
                </b-form-group>
            </b-form>
        </b-modal>

        <b-modal
                v-model="showRentModal"
                title="Сдача в аренду"
                ok-title="Сдать в аренду"
                cancel-title="Отмена"
                :busy="isSubmitting"
                :ok-disabled="isSubmitting || !rentalForm.renter_name"
                @ok.prevent="submitRentalForm"
        >
            <b-form @submit.stop.prevent="submitRentalForm">
                <b-form-group label="Арендатор">
                    <b-form-input v-model.trim="rentalForm.renter_name" required></b-form-input>
                </b-form-group>
                <b-form-group label="Номер договора">
                    <b-form-input v-model.trim="rentalForm.contract_number"></b-form-input>
                </b-form-group>
                <b-form-group label="Оператор-МОЛ">
                    <b-form-select v-model.number="rentalForm.operator_user_id" :options="userOptions"></b-form-select>
                </b-form-group>
                <b-form-group label="Ответственный">
                    <b-form-select v-model.number="rentalForm.responsible_user_id" :options="userOptions"></b-form-select>
                </b-form-group>
                <b-form-group label="Объект использования">
                    <b-form-select v-model.number="rentalForm.building_process_id" :options="objectOptions"></b-form-select>
                </b-form-group>
                <b-form-group label="Место использования вручную">
                    <b-form-input v-model.trim="rentalForm.use_location_text"></b-form-input>
                </b-form-group>
                <b-form-group label="Начало аренды">
                    <b-form-input v-model="rentalForm.started_at" type="datetime-local"></b-form-input>
                </b-form-group>
                <b-form-group label="Плановый возврат">
                    <b-form-input v-model="rentalForm.planned_return_at" type="date"></b-form-input>
                </b-form-group>
                <b-form-group label="Тип счетчика при передаче">
                    <b-form-select v-model="rentalForm.transfer_meter_type" :options="meterTypeOptions"></b-form-select>
                </b-form-group>
                <b-form-group label="Показание при передаче">
                    <b-form-input
                            v-model.number="rentalForm.transfer_meter_value"
                            type="number"
                            min="0"
                            step="0.001"
                    ></b-form-input>
                </b-form-group>
                <b-form-group label="Состояние при передаче">
                    <b-form-input v-model.trim="rentalForm.transfer_condition"></b-form-input>
                </b-form-group>
                <b-form-group label="Комментарий">
                    <AppTextarea v-model.trim="rentalForm.comment"></AppTextarea>
                </b-form-group>
            </b-form>
        </b-modal>

        <b-modal
                v-model="showReturnRentalModal"
                title="Возврат из аренды"
                ok-title="Вернуть"
                cancel-title="Отмена"
                :busy="isSubmitting"
                @ok.prevent="submitReturnRentalForm"
        >
            <b-form @submit.stop.prevent="submitReturnRentalForm">
                <b-form-group label="Дата возврата">
                    <b-form-input v-model="returnRentalForm.returned_at" type="datetime-local"></b-form-input>
                </b-form-group>
                <b-form-group label="Тип счетчика при возврате">
                    <b-form-select v-model="returnRentalForm.return_meter_type" :options="meterTypeOptions"></b-form-select>
                </b-form-group>
                <b-form-group label="Показание при возврате">
                    <b-form-input
                            v-model.number="returnRentalForm.return_meter_value"
                            type="number"
                            min="0"
                            step="0.001"
                    ></b-form-input>
                </b-form-group>
                <b-form-group label="Состояние при возврате">
                    <b-form-input v-model.trim="returnRentalForm.return_condition"></b-form-input>
                </b-form-group>
                <b-form-group label="Следующий статус">
                    <b-form-select v-model="returnRentalForm.next_status" :options="statusOptions"></b-form-select>
                </b-form-group>
                <b-form-group label="Следующее местонахождение">
                    <b-form-select v-model="returnRentalForm.next_location_type" :options="rentalLocationTypeOptions"></b-form-select>
                </b-form-group>
                <b-form-group label="Склад после возврата">
                    <b-form-select v-model.number="returnRentalForm.warehouse_id" :options="warehouseOptions"></b-form-select>
                </b-form-group>
                <b-form-group label="Объект после возврата">
                    <b-form-select v-model.number="returnRentalForm.building_process_id" :options="objectOptions"></b-form-select>
                </b-form-group>
                <b-form-group label="МОЛ после возврата">
                    <b-form-select v-model.number="returnRentalForm.responsible_user_id" :options="userOptions"></b-form-select>
                </b-form-group>
                <b-form-group label="Комментарий">
                    <AppTextarea v-model.trim="returnRentalForm.comment"></AppTextarea>
                </b-form-group>
            </b-form>
        </b-modal>

        <b-modal
                v-model="showArchiveModal"
                title="Архивация позиции"
                ok-title="В архив"
                ok-variant="danger"
                cancel-title="Отмена"
                :busy="isSubmitting"
                @ok.prevent="archiveUnit"
        >
            <b-form-group label="Комментарий к истории">
                <AppTextarea v-model.trim="archiveForm.history_comment"></AppTextarea>
            </b-form-group>
        </b-modal>

        <b-modal
                v-model="showPrimaryPhotoModal"
                title="Заглавное фото"
                size="lg"
                ok-only
                ok-title="Закрыть"
                :busy="isSubmitting"
                @hidden="resetPrimaryPhotoForm"
        >
            <b-alert
                    v-if="errorMessage"
                    show
                    variant="danger"
                    class="mb-3"
            >
                {{ errorMessage }}
            </b-alert>
            <b-form-group label="Загрузить новое фото">
                <MechanicAssetFileUploadPanel
                        v-model="primaryPhotoForm.files"
                        :disabled="isSubmitting"
                        accept="image/*"
                />
                <small class="text-muted d-block mt-1">
                    Можно загрузить несколько фото. Первое фото из выбранных станет заглавным.
                </small>
                <b-alert
                        v-if="primaryPhotoUpload.active"
                        show
                        variant="info"
                        class="mechanic-note-upload-status mt-2 mb-2"
                >
                    <div class="mechanic-note-upload-title">
                        <b-spinner small class="mr-2"></b-spinner>
                        <span>{{ primaryPhotoUploadText }}</span>
                    </div>
                    <b-progress
                            :value="primaryPhotoUploadPercent"
                            max="100"
                            height="8px"
                            animated
                            striped
                            class="mt-2"
                    ></b-progress>
                </b-alert>
                <b-button
                        size="sm"
                        variant="primary"
                        class="mt-2"
                        :disabled="isSubmitting || !primaryPhotoForm.files || !primaryPhotoForm.files.length"
                        @click="submitPrimaryPhotoForm"
                >
                    <b-spinner v-if="primaryPhotoUpload.active" small class="mr-1"></b-spinner>
                    <i v-else class="fas fa-upload mr-1"></i>
                    {{ primaryPhotoUpload.active ? 'Загружаю фото' : 'Загрузить фото и задать заглавное' }}
                </b-button>
            </b-form-group>

            <div v-if="photoRows.length">
                <h6 class="mb-2">Выбрать из загруженных фото</h6>
                <div class="mechanic-asset-photo-picker">
                    <div
                            v-for="file in photoRows"
                            :key="file.id"
                            class="mechanic-asset-photo-picker-item"
                            :class="{'mechanic-asset-photo-picker-item--active': isMainPhoto(file)}"
                    >
                        <img
                                :src="previewFileUrl(file, 220, 160)"
                                :alt="file.file_name"
                                loading="lazy"
                                decoding="async"
                        />
                        <b-badge
                                v-if="isMainPhoto(file)"
                                variant="warning"
                                class="mechanic-asset-photo-picker-badge"
                        >
                            Текущее
                        </b-badge>
                        <b-button
                                v-else
                                size="sm"
                                variant="light"
                                class="mechanic-asset-photo-picker-button"
                                :disabled="isSubmitting"
                                @click="setPrimaryPhotoFromModal(file)"
                        >
                            Выбрать
                        </b-button>
                    </div>
                </div>
            </div>
            <b-alert v-else show variant="light" class="mb-0">
                Загруженных фото пока нет.
            </b-alert>
        </b-modal>

    </div>
</template>

<script>
import EmployeeLink from '@/components/EmployeeLink.vue';
import MechanicAssetFileUploadPanel from './MechanicAssetFileUploadPanel.vue';
import MechanicAssetHistoryFeed from './components/MechanicAssetHistoryFeed.vue';
import {
    presentMechanicAssetHistory,
    presentMechanicAssetHistoryRow
} from '@/utils/mechanicAssetHistory';
import {
    MECHANIC_ASSET_ACCOUNTING_TYPE_LABELS as ACCOUNTING_TYPE_LABELS,
    MECHANIC_ASSET_CATALOG_CATEGORY_LABELS as CATALOG_CATEGORY_LABELS,
    getMechanicAssetStatusVariant
} from '@/utils/mechanicAssets';
import {
    fileDisplayName as getViewerFileDisplayName,
    fileFormatLabel as getViewerFileFormatLabel,
    fileIconClass as getViewerFileIconClass,
    isImageFile as isViewerImageFile,
    withQueryParams
} from '@/utils/fileViewer';
import {uploadFileWithRetry} from '@/utils/fileUploadQueue';
import {notifyInstallPromotionSuccess} from '@/utils/installPromotion';

const HISTORY_ACTION_LABELS = {
    created: `Создано`,
    updated: `Изменено`,
    archived: `Архив`,
    note_added: `Примечание`,
    file_deleted: `Файл удален`,
    primary_photo_changed: `Заглавное фото`,
    fuel_norm_created: `Норма топлива`,
    fuel_norm_updated: `Норма изменена`,
    fuel_norm_deleted: `Норма удалена`,
    meter_recorded: `Показание счетчика`,
    rented_out: `Сдано в аренду`,
    returned_from_rent: `Возврат из аренды`,
    request_repair_created: `Заявка на ремонт`,
    request_repair_taken: `Ремонт взят в работу`,
    request_repair_cancelled: `Ремонт отменен`,
    request_receipt: `Приход по заявке`,
    request_quantity_shipment_adjusted: `Остаток по заявке скорректирован`,
    request_issue_to_object: `Выдано на объект`,
    request_move_to_object: `Перемещено на объект`,
    request_move_to_warehouse: `Перемещено на склад`,
    request_return_to_warehouse: `Возвращено на склад`,
    request_transfer_started: `Отправлено`,
    request_transfer_updated: `Маршрут изменён`,
    request_transfer_cancelled: `Перемещение отменено`,
    request_accept_to_repair: `Принято в ремонт`,
    request_finish_repair: `Ремонт завершён`,
    request_rent_out: `Сдано в аренду`,
    request_return_from_rent: `Возвращено из аренды`,
    import_1c_rb: `Импорт позиции 1С РБ`,
    import_1c_rb_catalog_consolidated: `Объединение группы 1С РБ`,
    import_facekit_positions: `FaceKIT: импорт позиции`,
    import_facekit_transfer: `FaceKIT: перемещение`,
    import_facekit_creation: `FaceKIT: создание`,
    import_facekit_inventory: `FaceKIT: инвентаризация`,
    import_facekit_spending: `FaceKIT: списание`,
    import_facekit_request: `FaceKIT: заявка`,
    import_facekit_transaction: `FaceKIT: операция`,
    demo_seed: `Демонстрационные данные`
};
const HIDDEN_FILE_ADDED_HISTORY_ACTIONS = [`files_added`, `note_files_added`];

const WORK_LINKED_FUEL_NORM_TYPES = [`per_operation`, `per_work_unit`, `mixed`];

function emptyUnitForm() {
    return {
        catalog_id: null,
        display_name: null,
        status: `stock`,
        location_type: `unknown`,
        warehouse_id: null,
        building_process_id: null,
        object_group_id: null,
        responsible_user_id: null,
        quantity: 1,
        accounting_type: `inventory`,
        unit: null,
        technical_condition: null,
        inventory_number: null,
        serial_number: null,
        vin: null,
        plate_number: null,
        current_meter_type: null,
        current_meter_value: null,
        accounting_company_name: null,
        owner_kind: `own`,
        owner_name: null,
        comment: null,
        history_comment: null
    };
}

function emptyFuelNormForm() {
    return {
        id: null,
        fuel_type_id: null,
        norm_type: `per_moto_hour`,
        value: null,
        value_unit: `л`,
        work_type_id: null,
        work_type_name: null,
        work_unit: null,
        effective_from: todayInputValue(),
        effective_to: null,
        source: `manual`,
        basis_document: null,
        is_approved: false,
        comment: null
    };
}

function emptyNoteForm() {
    return {
        created_note_id: null,
        note_type: `general`,
        text: null,
        files: []
    };
}

function todayInputValue() {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, `0`);
    const day = String(date.getDate()).padStart(2, `0`);
    return `${year}-${month}-${day}`;
}

function emptyMeterForm(unit) {
    return {
        meter_type: unit && unit.current_meter_type ? unit.current_meter_type : `moto_hour`,
        value: unit && unit.current_meter_value !== null && unit.current_meter_value !== undefined
            ? unit.current_meter_value
            : null,
        fixed_at: null,
        comment: null
    };
}

function emptyRentalForm(unit) {
    return {
        renter_name: null,
        contract_number: null,
        operator_user_id: unit ? unit.responsible_user_id : null,
        responsible_user_id: unit ? unit.responsible_user_id : null,
        building_process_id: unit ? unit.building_process_id : null,
        use_location_text: null,
        started_at: null,
        planned_return_at: null,
        transfer_meter_type: unit && unit.current_meter_type ? unit.current_meter_type : null,
        transfer_meter_value: unit && unit.current_meter_value !== null && unit.current_meter_value !== undefined
            ? unit.current_meter_value
            : null,
        transfer_condition: unit ? unit.technical_condition : null,
        comment: null
    };
}

function emptyReturnRentalForm(unit) {
    return {
        returned_at: null,
        return_meter_type: unit && unit.current_meter_type ? unit.current_meter_type : null,
        return_meter_value: unit && unit.current_meter_value !== null && unit.current_meter_value !== undefined
            ? unit.current_meter_value
            : null,
        return_condition: unit ? unit.technical_condition : null,
        next_status: `stock`,
        next_location_type: `warehouse`,
        warehouse_id: unit ? unit.warehouse_id : null,
        building_process_id: null,
        responsible_user_id: unit ? unit.responsible_user_id : null,
        comment: null
    };
}

export default {
    name: `MechanicAssetCard`,
    components: {
        EmployeeLink,
        MechanicAssetFileUploadPanel,
        MechanicAssetHistoryFeed
    },
    props: {
        unit: {
            type: Object,
            default: null
        },
        historyRows: {
            type: Array,
            default: () => []
        },
        noteRows: {
            type: Array,
            default: () => []
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            errorMessage: null,
            isSubmitting: false,
            activeTabIndex: 0,
            historyViewMode: `timeline`,
            showEditUnitModal: false,
            showNoteModal: false,
            showFuelNormModal: false,
            showDeleteFuelNormModal: false,
            showMeterModal: false,
            showRentModal: false,
            showReturnRentalModal: false,
            showArchiveModal: false,
            showPrimaryPhotoModal: false,
            activePhotoId: null,
            modalHistory: {
                pushed: false,
                closingFromPopstate: false,
                restoringHistory: false
            },
            unitForm: emptyUnitForm(),
            accountingTypeOptions: Object.keys(ACCOUNTING_TYPE_LABELS).map(value => ({
                value,
                text: ACCOUNTING_TYPE_LABELS[value]
            })),
            noteForm: emptyNoteForm(),
            noteUpload: {
                active: false,
                stage: null,
                currentIndex: 0,
                total: 0,
                fileName: null,
                fileProgress: 0
            },
            fuelNormForm: emptyFuelNormForm(),
            currentFuelNorm: null,
            fileForm: {
                files: []
            },
            fileUpload: {
                active: false,
                stage: null,
                currentIndex: 0,
                total: 0,
                fileName: null,
                fileProgress: 0
            },
            primaryPhotoForm: {
                files: []
            },
            primaryPhotoUpload: {
                active: false,
                stage: null,
                currentIndex: 0,
                total: 0,
                fileName: null,
                fileProgress: 0
            },
            meterForm: emptyMeterForm(null),
            rentalForm: emptyRentalForm(null),
            returnRentalForm: emptyReturnRentalForm(null),
            archiveForm: {
                history_comment: null
            },
            meterTypeOptions: [
                {value: null, text: `Не указан`},
                {value: `km`, text: `Километры`},
                {value: `moto_hour`, text: `Моточасы`}
            ],
            noteTypeOptions: [
                {value: `general`, text: `Общее`},
                {value: `inspection`, text: `Осмотр`},
                {value: `repair`, text: `Ремонт`},
                {value: `fuel`, text: `Топливо`},
                {value: `rental`, text: `Аренда`}
            ],
            fuelNormFields: [
                {key: `fuel_type_name`, label: `Топливо`},
                {key: `norm_type`, label: `Тип нормы`},
                {key: `value`, label: `Значение`},
                {key: `period`, label: `Период`},
                {key: `work_type_name_resolved`, label: `Группа работ`},
                {key: `actions`, label: ``, class: `text-nowrap text-right`}
            ],
            fileFields: [
                {key: `file_name`, label: `Файл`},
                {key: `file_kind`, label: `Тип`},
                {key: `primary`, label: `Фото`, class: `text-nowrap`},
                {key: `uploaded_by_name`, label: `Кто загрузил`},
                {key: `created_at`, label: `Дата`, class: `text-nowrap`}
            ],
            rentalFields: [
                {key: `renter_name`, label: `Арендатор`},
                {key: `period`, label: `Период`},
                {key: `status`, label: `Статус`},
                {key: `contract_number`, label: `Договор`}
            ],
            historyFields: [
                {key: `created_at`, label: `Дата`, class: `text-nowrap`},
                {key: `action`, label: `Событие`},
                {key: `changed_by_name`, label: `Кто изменил`},
                {key: `request`, label: `Заявка`},
                {key: `changes`, label: `Изменения`},
                {key: `comment`, label: `Комментарий`}
            ],
            noteFields: [
                {key: `created_at`, label: `Дата`, class: `text-nowrap`},
                {key: `note_type`, label: `Тип`},
                {key: `author_name`, label: `Автор`},
                {key: `text`, label: `Текст`},
                {key: `files`, label: `Файлы`}
            ]
        };
    },
    computed: {
        mechanicState() {
            return this.$store.state.mechanicAssets;
        },
        legacyCanWrite() {
            if (!this.$can) {
                return false;
            }
            return this.$can(`manage`, `mechanicAssets`)
                || this.$can(`write`, `mechanicAssets`)
                || this.$can(`update`, `mechanicAssets`)
                || this.$can(`create`, `mechanicAssets`);
        },
        canWrite() {
            return (this.unit && this.unit.can_update === true) || this.legacyCanWrite;
        },
        canDelete() {
            return (this.unit && this.unit.can_delete === true) || this.legacyCanWrite;
        },
        canReadHistory() {
            return this.unit && this.unit.can_read_history === true;
        },
        fileTableFields() {
            const fields = this.fileFields.slice();
            fields.push({key: `actions`, label: ``, class: `text-nowrap text-right`});
            return fields;
        },
        statusOptions() {
            return this.mechanicState.statuses;
        },
        locationTypeOptions() {
            return this.mechanicState.locationTypes;
        },
        rentalLocationTypeOptions() {
            return this.locationTypeOptions.filter(option => option.value !== `object_group`);
        },
        objectGroupOptions() {
            return (this.mechanicState.objectGroups || []).map(group => ({value: group.id, text: group.name}));
        },
        assignableObjectGroupOptions() {
            return (this.mechanicState.objectGroups || []).filter(group => group.can_assign)
                .map(group => ({value: group.id, text: group.name}));
        },
        fuelNormTypeOptions() {
            return this.mechanicState.fuelNormTypes;
        },
        warehouseOptions() {
            return [{value: null, text: `Не указан`}].concat(this.mechanicState.warehouses.map(row => ({
                value: row.id,
                text: row.name
            })));
        },
        catalogOptions() {
            return [{value: null, text: `Выберите группу техники`}].concat(this.mechanicState.catalog.map(row => ({
                value: row.id,
                text: this.catalogOptionText(row)
            })));
        },
        objectOptions() {
            const processes = this.$store.state.objectsInProcess || [];
            const options = processes.map(process => {
                const name = process.object_name
                    || (process.bo && process.bo.name)
                    || `Процесс ${process.building_process_id}`;
                const contract = process.contract_name ? ` (${process.contract_name})` : ``;
                return {
                    value: process.building_process_id,
                    text: `${name}${contract}`
                };
            }).sort((a, b) => a.text.localeCompare(b.text, `ru`));
            return [{value: null, text: `Не указан`}].concat(options);
        },
        userOptions() {
            const users = this.$store.state.users || [];
            const options = users
                .filter(user => user.active && !user.isBot)
                .map(user => ({
                    value: user.user_id,
                    text: this.userName(user)
                }))
                .sort((a, b) => a.text.localeCompare(b.text, `ru`));
            return [{value: null, text: `Не указан`}].concat(options);
        },
        fuelTypeOptions() {
            const fuelTypes = this.$store.state.fuelTypes || [];
            return [{value: null, text: `Выберите топливо`}].concat(fuelTypes.map(row => ({
                value: row.id,
                text: row.unit ? `${row.name} (${row.unit})` : row.name
            })));
        },
        workTypeOptions() {
            const workTypes = this.$store.state.workTypes || [];
            const options = workTypes.map(row => ({
                value: row.work_id,
                text: row.unit_of_measurement ? `${row.name} (${row.unit_of_measurement})` : row.name
            })).sort((a, b) => a.text.localeCompare(b.text, `ru`));
            return [{value: null, text: `Не указан`}].concat(options);
        },
        fuelNormRows() {
            return this.unit && Array.isArray(this.unit.FuelNorms) ? this.unit.FuelNorms : [];
        },
        fileRows() {
            const rows = this.unit && Array.isArray(this.unit.Files) ? this.unit.Files : [];
            return rows.slice().sort((a, b) => {
                if (a.is_primary_photo !== b.is_primary_photo) {
                    return a.is_primary_photo ? -1 : 1;
                }
                return Number(b.id || 0) - Number(a.id || 0);
            });
        },
        noteList() {
            const rows = Array.isArray(this.noteRows) ? this.noteRows : [];
            return rows.slice().sort((a, b) => {
                const dateDiff = new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
                return dateDiff || (Number(b.id || 0) - Number(a.id || 0));
            });
        },
        visibleHistoryRows() {
            const rows = Array.isArray(this.historyRows) ? this.historyRows : [];
            return rows.filter(row => !HIDDEN_FILE_ADDED_HISTORY_ACTIONS.includes(row && row.action));
        },
        historyPresentationContext() {
            return {
                unit: this.unit && this.unit.unit,
                meterType: this.unit && this.unit.current_meter_type,
                statusOptions: this.statusOptions,
                locationTypeOptions: this.locationTypeOptions,
                catalogOptions: this.catalogOptions,
                warehouseOptions: this.warehouseOptions,
                objectOptions: this.objectOptions,
                objectGroupOptions: this.objectGroupOptions,
                userOptions: this.userOptions
            };
        },
        readableHistoryEvents() {
            return presentMechanicAssetHistory(this.visibleHistoryRows, this.historyPresentationContext);
        },
        tabOrder() {
            const tabs = [`card`];
            if (!this.unitIsQuantitative) {
                tabs.push(`fuel`, `meter`, `rental`);
            }
            tabs.push(`history`, `notes`, `files`);
            return tabs;
        },
        isNoteSubmitDisabled() {
            if (this.isSubmitting || this.noteUpload.active || !this.noteForm.text) {
                return true;
            }
            return Boolean(this.noteForm.created_note_id) &&
                (!Array.isArray(this.noteForm.files) || !this.noteForm.files.length);
        },
        noteModalOkTitle() {
            return this.noteForm.created_note_id ? `Догрузить файлы` : `Добавить`;
        },
        noteUploadPercent() {
            if (!this.noteUpload.active || this.noteUpload.stage === `create`) {
                return 5;
            }
            const total = Number(this.noteUpload.total) || 0;
            if (!total) {
                return 0;
            }
            const completed = Math.max(0, Number(this.noteUpload.currentIndex || 1) - 1);
            const current = Math.max(0, Math.min(100, Number(this.noteUpload.fileProgress) || 0)) / 100;
            return Math.max(1, Math.min(100, Math.round(((completed + current) / total) * 100)));
        },
        noteUploadText() {
            if (this.noteUpload.stage === `create`) {
                return `Создаю примечание`;
            }
            if (this.noteUpload.stage === `refresh`) {
                return `Обновляю карточку`;
            }
            if (this.noteUpload.stage === `upload`) {
                const parts = [
                    `Загружаю файл ${this.noteUpload.currentIndex || 1} из ${this.noteUpload.total || 1}`
                ];
                if (this.noteUpload.fileName) {
                    parts.push(this.noteUpload.fileName);
                }
                return parts.join(`: `);
            }
            return `Сохраняю`;
        },
        fileUploadPercent() {
            if (!this.fileUpload.active || this.fileUpload.stage === `refresh`) {
                return this.fileUpload.active ? 100 : 0;
            }
            const total = Number(this.fileUpload.total) || 0;
            if (!total) {
                return 0;
            }
            const completed = Math.max(0, Number(this.fileUpload.currentIndex || 1) - 1);
            const current = Math.max(0, Math.min(100, Number(this.fileUpload.fileProgress) || 0)) / 100;
            return Math.max(1, Math.min(100, Math.round(((completed + current) / total) * 100)));
        },
        fileUploadText() {
            if (this.fileUpload.stage === `refresh`) {
                return `Обновляю карточку`;
            }
            const parts = [
                `Загружаю файл ${this.fileUpload.currentIndex || 1} из ${this.fileUpload.total || 1}`
            ];
            if (this.fileUpload.fileName) {
                parts.push(this.fileUpload.fileName);
            }
            return parts.join(`: `);
        },
        primaryPhotoUploadPercent() {
            if (!this.primaryPhotoUpload.active) {
                return 0;
            }
            if (this.primaryPhotoUpload.stage === `refresh`) {
                return 100;
            }
            const total = Number(this.primaryPhotoUpload.total) || 0;
            if (!total) {
                return 0;
            }
            const completed = Math.max(0, Number(this.primaryPhotoUpload.currentIndex || 1) - 1);
            const current = Math.max(0, Math.min(100, Number(this.primaryPhotoUpload.fileProgress) || 0)) / 100;
            return Math.max(1, Math.min(100, Math.round(((completed + current) / total) * 100)));
        },
        primaryPhotoUploadText() {
            if (this.primaryPhotoUpload.stage === `refresh`) {
                return `Обновляю карточку`;
            }
            const parts = [
                `Загружаю фото ${this.primaryPhotoUpload.currentIndex || 1} из ${this.primaryPhotoUpload.total || 1}`
            ];
            if (this.primaryPhotoUpload.fileName) {
                parts.push(this.primaryPhotoUpload.fileName);
            }
            return parts.join(`: `);
        },
        mainPhoto() {
            const activePhotoId = Number(this.activePhotoId);
            if (Number.isInteger(activePhotoId) && activePhotoId > 0) {
                const activePhoto = this.photoRows.find(file => Number(file.id) === activePhotoId);
                if (activePhoto) {
                    return activePhoto;
                }
            }

            const primaryPhotoId = this.unit && this.unit.primary_photo && this.unit.primary_photo.id
                ? Number(this.unit.primary_photo.id)
                : null;
            if (primaryPhotoId) {
                const primaryPhoto = this.photoRows.find(file => Number(file.id) === primaryPhotoId);
                if (primaryPhoto) {
                    return primaryPhoto;
                }
            }

            return this.photoRows.find(file => file.is_primary_photo)
                || (this.unit && this.unit.primary_photo && this.isImageFile(this.unit.primary_photo) ? this.unit.primary_photo : null)
                || this.photoRows[0]
                || null;
        },
        mainPhotoIndex() {
            if (!this.mainPhoto) {
                return 0;
            }
            const index = this.photoRows.findIndex(file => Number(file.id) === Number(this.mainPhoto.id));
            return index >= 0 ? index : 0;
        },
        photoRows() {
            const seen = {};
            const rows = [];
            const remember = file => {
                if (!file || !file.id || seen[file.id] || !this.isImageFile(file)) {
                    return;
                }
                seen[file.id] = true;
                rows.push(file);
            };
            this.fileRows.forEach(remember);
            this.noteList.forEach(note => {
                this.noteFiles(note).forEach(remember);
            });
            return rows;
        },
        viewerRows() {
            const seen = {};
            const rows = [];
            const remember = file => {
                if (!file || !file.id || seen[file.id]) {
                    return;
                }
                seen[file.id] = true;
                rows.push(file);
            };
            this.fileRows.forEach(remember);
            this.noteList.forEach(note => {
                this.noteFiles(note).forEach(remember);
            });
            return rows;
        },
        viewerFiles() {
            return this.viewerRows.map(file => {
                const image = this.isImageFile(file);
                return {
                    id: file.id,
                    name: this.fileDisplayName(file),
                    url: this.fileUrl(file),
                    downloadUrl: this.fileUrl(file),
                    thumbnailUrl: image ? this.previewFileUrl(file, 128, 96) : null,
                    mimeType: file.mime_type || file.mimeType || file.type,
                    size: file.file_size || file.size,
                    isImage: image,
                    raw: file
                };
            });
        },
        rentalRows() {
            return this.unit && Array.isArray(this.unit.Rentals) ? this.unit.Rentals : [];
        },
        activeRental() {
            if (!this.unit) {
                return null;
            }
            if (this.unit.ActiveRental) {
                return this.unit.ActiveRental;
            }
            return this.rentalRows.find(row => row.status === `active`) || null;
        },
        externalIds() {
            return this.unit && Array.isArray(this.unit.ExternalIds) ? this.unit.ExternalIds : [];
        },
        activeTransfer() {
            if (!this.unit) {
                return null;
            }
            if (this.unit.status !== `in_transfer`) {
                return null;
            }
            const transfer = this.unit.active_transfer || this.unit.transfer_state || null;
            return transfer && transfer.active === true ? transfer : null;
        },
        isFuelNormValid() {
            return Boolean(this.fuelNormForm.fuel_type_id
                && this.fuelNormForm.norm_type
                && Number(this.fuelNormForm.value) > 0
                && this.fuelNormForm.value_unit);
        },
        fuelNormUsesWorkGroup() {
            return WORK_LINKED_FUEL_NORM_TYPES.indexOf(this.fuelNormForm.norm_type) !== -1;
        },
        unitIsQuantitative() {
            return this.unit && this.unit.accounting_type === `quantity`;
        },
        selectedUnitFormCatalog() {
            const id = Number(this.unitForm.catalog_id);
            if (!Number.isInteger(id) || id <= 0) {
                return null;
            }
            return (this.mechanicState.catalog || []).find(row => Number(row.id) === id) || null;
        },
        unitFormIsQuantitative() {
            return this.unitForm.accounting_type === `quantity`;
        },
        isUnitFormValid() {
            if (this.unitForm.location_type === `object_group` && !this.unitForm.object_group_id) return false;
            if (this.isSubmitting || !this.unitForm.catalog_id) {
                return false;
            }
            if (this.unitFormIsQuantitative) {
                return Number(this.unitForm.quantity) >= 0 && Boolean(this.unitForm.unit);
            }
            return true;
        },
        unitUsesWarehouse() {
            return [`warehouse`, `repair`, `external_repair`].indexOf(this.unitForm.location_type) !== -1;
        },
        unitUsesObject() {
            return this.unitForm.location_type === `object`;
        },
        isMeterValid() {
            return Boolean(this.meterForm.meter_type
                && this.meterForm.value !== null
                && this.meterForm.value !== ``
                && Number(this.meterForm.value) >= 0);
        }
    },
    watch: {
        unit(newValue, oldValue) {
            const newId = newValue && newValue.id ? Number(newValue.id) : null;
            const oldId = oldValue && oldValue.id ? Number(oldValue.id) : null;
            if (newId !== oldId) {
                this.activePhotoId = null;
            }
        },
        'unitForm.location_type'() {
            this.normalizeUnitLocation();
        },
        'unitForm.catalog_id'() {
            this.applyUnitCatalogDefaults();
        },
        'unitForm.accounting_type'() {
            this.applyUnitAccountingDefaults();
        },
        'fuelNormForm.norm_type'() {
            this.normalizeFuelNormWorkFields();
        },
        showEditUnitModal(value) {
            this.handleCardModalVisibilityChange(value);
        },
        showNoteModal(value) {
            this.handleCardModalVisibilityChange(value);
        },
        showFuelNormModal(value) {
            this.handleCardModalVisibilityChange(value);
        },
        showDeleteFuelNormModal(value) {
            this.handleCardModalVisibilityChange(value);
        },
        showMeterModal(value) {
            this.handleCardModalVisibilityChange(value);
        },
        showRentModal(value) {
            this.handleCardModalVisibilityChange(value);
        },
        showReturnRentalModal(value) {
            this.handleCardModalVisibilityChange(value);
        },
        showArchiveModal(value) {
            this.handleCardModalVisibilityChange(value);
        },
        showPrimaryPhotoModal(value) {
            this.handleCardModalVisibilityChange(value);
        }
    },
    mounted() {
        this.$store.dispatch(`getTypesOfFuel`).catch(() => {});
        this.$store.dispatch(`getWorkTypes`).catch(() => {});
        if (typeof window !== `undefined`) {
            window.addEventListener(`popstate`, this.handleCardModalPopState);
        }
    },
    beforeDestroy() {
        if (typeof window !== `undefined`) {
            window.removeEventListener(`popstate`, this.handleCardModalPopState);
        }
    },
    methods: {
        cardModalKeys() {
            return [
                `showEditUnitModal`,
                `showNoteModal`,
                `showFuelNormModal`,
                `showDeleteFuelNormModal`,
                `showMeterModal`,
                `showRentModal`,
                `showReturnRentalModal`,
                `showArchiveModal`,
                `showPrimaryPhotoModal`
            ];
        },
        hasOpenCardModal() {
            return this.cardModalKeys().some(key => this[key]);
        },
        closeOpenCardModal() {
            const openKey = this.cardModalKeys().slice().reverse().find(key => this[key]);
            if (openKey) {
                this[openKey] = false;
            }
        },
        handleCardModalVisibilityChange(isOpen) {
            if (isOpen) {
                this.pushCardModalHistoryState();
                return;
            }

            this.$nextTick(() => {
                if (
                    this.hasOpenCardModal()
                    || !this.modalHistory.pushed
                    || this.modalHistory.closingFromPopstate
                    || typeof window === `undefined`
                    || !window.history
                    || typeof window.history.back !== `function`
                ) {
                    return;
                }
                this.modalHistory.pushed = false;
                this.modalHistory.restoringHistory = true;
                window.__mechanicAssetCardSuppressTelegramBack = true;
                window.history.back();
            });
        },
        pushCardModalHistoryState() {
            if (
                this.modalHistory.pushed
                || typeof window === `undefined`
                || !window.history
                || typeof window.history.pushState !== `function`
            ) {
                return;
            }
            window.history.pushState({
                mechanicAssetCardModal: true,
                mechanicAssetId: this.unit && this.unit.id ? this.unit.id : null
            }, document.title, window.location.href);
            this.modalHistory.pushed = true;
        },
        handleCardModalPopState() {
            if (this.modalHistory.restoringHistory) {
                this.modalHistory.restoringHistory = false;
                return;
            }
            if (!this.hasOpenCardModal() && !this.modalHistory.pushed) {
                return;
            }
            this.modalHistory.closingFromPopstate = true;
            this.modalHistory.pushed = false;
            this.closeOpenCardModal();
            this.$nextTick(() => {
                this.modalHistory.closingFromPopstate = false;
            });
        },
        openEditUnitModal() {
            this.unitForm = this.createUnitForm(this.unit);
            this.showEditUnitModal = true;
        },
        openNoteModal() {
            this.noteForm = emptyNoteForm();
            this.errorMessage = null;
            this.resetNoteUpload();
            this.switchTab(`notes`);
            this.showNoteModal = true;
        },
        resetNoteForm() {
            if (!this.isSubmitting) {
                this.noteForm = emptyNoteForm();
                this.resetNoteUpload();
            }
        },
        tabIndex(name) {
            const index = this.tabOrder.indexOf(name);
            return index === -1 ? 0 : index;
        },
        switchTab(name) {
            this.activeTabIndex = this.tabIndex(name);
        },
        openFuelNormModal(norm) {
            this.currentFuelNorm = norm || null;
            this.fuelNormForm = norm ? this.createFuelNormForm(norm) : emptyFuelNormForm();
            this.showFuelNormModal = true;
        },
        openDeleteFuelNormModal(norm) {
            this.currentFuelNorm = norm;
            this.showDeleteFuelNormModal = true;
        },
        openMeterModal() {
            this.meterForm = emptyMeterForm(this.unit);
            this.showMeterModal = true;
        },
        openRentModal() {
            this.rentalForm = emptyRentalForm(this.unit);
            this.showRentModal = true;
        },
        openReturnRentalModal() {
            this.returnRentalForm = emptyReturnRentalForm(this.unit);
            this.showReturnRentalModal = true;
        },
        openArchiveModal() {
            this.archiveForm = {history_comment: null};
            this.showArchiveModal = true;
        },
        openPrimaryPhotoModal() {
            this.primaryPhotoForm = {files: []};
            this.resetPrimaryPhotoUpload();
            this.showPrimaryPhotoModal = true;
        },
        resetPrimaryPhotoForm() {
            this.primaryPhotoForm = {files: []};
            this.resetPrimaryPhotoUpload();
        },
        selectHeaderPhoto(file) {
            if (!file || !file.id || !this.isImageFile(file)) {
                return;
            }
            this.activePhotoId = Number(file.id);
        },
        openPhotoViewer(file) {
            if (!file || !file.id) {
                return;
            }
            if (!this.$fileViewer) {
                window.open(this.fileUrl(file), `_blank`);
                return;
            }
            this.$fileViewer.open(this.viewerFiles, {
                startId: file.id,
                source: `mechanic-assets`,
                primaryAction: {
                    label: `Сделать заглавным`,
                    icon: `fas fa-star`,
                    visible: viewerFile => this.canWrite
                        && viewerFile
                        && viewerFile.raw
                        && this.isImageFile(viewerFile.raw)
                        && !this.isMainPhoto(viewerFile.raw),
                    disabled: () => this.isSubmitting,
                    handler: viewerFile => this.setPrimaryPhotoFromViewer(viewerFile)
                }
            });
        },
        async setPrimaryPhotoFromViewer(viewerFile) {
            const file = viewerFile && viewerFile.raw ? viewerFile.raw : viewerFile;
            await this.setPrimaryPhoto(file);
        },
        handleFilePreviewClick(event, file) {
            if (!file || !file.id) {
                return;
            }
            if (event && event.preventDefault) {
                event.preventDefault();
            }
            this.openPhotoViewer(file);
        },
        resetNoteUpload() {
            this.noteUpload = {
                active: false,
                stage: null,
                currentIndex: 0,
                total: 0,
                fileName: null,
                fileProgress: 0
            };
        },
        resetFileUpload() {
            this.fileUpload = {
                active: false,
                stage: null,
                currentIndex: 0,
                total: 0,
                fileName: null,
                fileProgress: 0
            };
        },
        resetPrimaryPhotoUpload() {
            this.primaryPhotoUpload = {
                active: false,
                stage: null,
                currentIndex: 0,
                total: 0,
                fileName: null,
                fileProgress: 0
            };
        },
        async submitUnitForm() {
            if (!this.unit || !this.unit.id || !this.isUnitFormValid) {
                return;
            }
            this.isSubmitting = true;
            this.errorMessage = null;
            try {
                await this.$store.dispatch(`mechanicAssets/updateUnit`, {
                    id: this.unit.id,
                    payload: this.sanitizeUnitPayload(this.unitForm)
                });
                notifyInstallPromotionSuccess({source: 'mechanic-asset-updated'});
                this.showEditUnitModal = false;
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка сохранения карточки`);
            } finally {
                this.isSubmitting = false;
            }
        },
        async submitNoteForm() {
            if (!this.unit || !this.unit.id || this.isNoteSubmitDisabled) {
                return;
            }
            this.isSubmitting = true;
            this.errorMessage = null;
            this.noteUpload = {
                active: true,
                stage: this.noteForm.created_note_id ? `upload` : `create`,
                currentIndex: 0,
                total: Array.isArray(this.noteForm.files) ? this.noteForm.files.length : 0,
                fileName: null,
                fileProgress: 0
            };
            try {
                const files = Array.isArray(this.noteForm.files) ? this.noteForm.files : [];
                let noteId = this.noteForm.created_note_id;
                let noteCreated = false;
                if (!noteId) {
                    const note = await this.$store.dispatch(`mechanicAssets/addNote`, {
                        id: this.unit.id,
                        refresh: false,
                        payload: this.cleanPayload({
                            note_type: this.noteForm.note_type,
                            text: this.noteForm.text
                        })
                    });
                    noteId = note && note.id ? note.id : null;
                    noteCreated = Boolean(noteId);
                    this.noteForm.created_note_id = noteId;
                }
                if (!noteId) {
                    throw new Error(`Не удалось определить ID примечания`);
                }

                const failedFiles = await this.uploadNoteFilesSequential(noteId, files);
                if (noteCreated || failedFiles.length < files.length) {
                    notifyInstallPromotionSuccess({source: 'mechanic-asset-note-saved'});
                }
                this.noteUpload.stage = `refresh`;
                let refreshError = null;
                try {
                    await this.$store.dispatch(`mechanicAssets/openUnit`, this.unit.id);
                } catch (err) {
                    refreshError = err;
                    console.error(`Не удалось обновить карточку после добавления примечания:`, err);
                }

                if (failedFiles.length) {
                    this.noteForm.files = failedFiles;
                    this.errorMessage = `Примечание добавлено, но не загрузились файлы: ${failedFiles.map(file => this.fileDisplayName(file)).join(', ')}`;
                    if (refreshError) {
                        this.errorMessage += `. Карточку нужно обновить вручную после восстановления связи.`;
                    }
                    this.switchTab(`notes`);
                    return;
                }
                if (refreshError) {
                    this.noteForm.files = [];
                    this.errorMessage = `Примечание добавлено, но карточку не удалось обновить. Обновите страницу после восстановления связи.`;
                    this.switchTab(`notes`);
                    return;
                }

                this.noteForm = emptyNoteForm();
                this.showNoteModal = false;
                this.switchTab(`notes`);
                this.$nextTick(() => {
                    if (this.$refs.notesPanel && this.$refs.notesPanel.scrollIntoView) {
                        this.$refs.notesPanel.scrollIntoView({block: `nearest`});
                    }
                });
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка добавления примечания`);
            } finally {
                this.resetNoteUpload();
                this.isSubmitting = false;
            }
        },
        async uploadNoteFilesSequential(noteId, files) {
            const queue = Array.isArray(files) ? files.filter(Boolean) : [];
            const failedFiles = [];
            if (!queue.length) {
                return failedFiles;
            }

            this.noteUpload.total = queue.length;
            this.noteUpload.stage = `upload`;

            for (let index = 0; index < queue.length; index += 1) {
                const file = queue[index];
                this.noteUpload.currentIndex = index + 1;
                this.noteUpload.fileName = this.fileDisplayName(file);
                this.noteUpload.fileProgress = 0;
                try {
                    await uploadFileWithRetry(() => {
                        const formData = new FormData();
                        formData.append(`files`, file);
                        return this.$store.dispatch(`mechanicAssets/uploadNoteFiles`, {
                            id: this.unit.id,
                            noteId,
                            payload: formData,
                            refresh: false,
                            config: {
                                ignoreModal: true,
                                onUploadProgress: event => {
                                    const total = Number(event.total || file.size || 0);
                                    if (total > 0) {
                                        this.noteUpload.fileProgress = Math.min(100, Math.round((event.loaded / total) * 100));
                                    }
                                }
                            }
                        });
                    }, file);
                    this.noteUpload.fileProgress = 100;
                } catch (err) {
                    failedFiles.push(file);
                    console.error(`Не удалось загрузить файл примечания:`, err);
                }
            }

            return failedFiles;
        },
        async uploadAssetFilesSequential(files, options = {}) {
            const queue = Array.isArray(files) ? files.filter(Boolean) : [];
            const failedFiles = [];
            const uploadState = options.uploadState || this.fileUpload;
            const makeFirstPrimary = options.makeFirstPrimary === true;
            if (!queue.length) {
                return failedFiles;
            }

            uploadState.total = queue.length;
            uploadState.stage = `upload`;

            for (let index = 0; index < queue.length; index += 1) {
                const file = queue[index];
                uploadState.currentIndex = index + 1;
                uploadState.fileName = this.fileDisplayName(file);
                uploadState.fileProgress = 0;
                try {
                    await uploadFileWithRetry(() => {
                        const formData = new FormData();
                        formData.append(`files`, file);
                        if (makeFirstPrimary && index === 0) {
                            formData.append(`is_primary_photo`, `true`);
                        }
                        return this.$store.dispatch(`mechanicAssets/uploadFiles`, {
                            id: this.unit.id,
                            payload: formData,
                            refresh: false,
                            config: {
                                ignoreModal: true,
                                onUploadProgress: event => {
                                    const total = Number(event.total || file.size || 0);
                                    if (total > 0) {
                                        uploadState.fileProgress = Math.min(100, Math.round((event.loaded / total) * 100));
                                    }
                                }
                            }
                        });
                    }, file);
                    uploadState.fileProgress = 100;
                } catch (err) {
                    failedFiles.push(file);
                    console.error(`Не удалось загрузить файл техники:`, err);
                }
            }

            return failedFiles;
        },
        async submitFileForm() {
            const files = Array.isArray(this.fileForm.files) ? this.fileForm.files : [];
            if (!this.unit || !this.unit.id || this.isSubmitting || !files.length) {
                return;
            }
            this.isSubmitting = true;
            this.errorMessage = null;
            this.fileUpload = {
                active: true,
                stage: `upload`,
                currentIndex: 0,
                total: files.length,
                fileName: null,
                fileProgress: 0
            };
            try {
                const failedFiles = await this.uploadAssetFilesSequential(files, {
                    uploadState: this.fileUpload
                });
                if (failedFiles.length < files.length) {
                    notifyInstallPromotionSuccess({source: 'mechanic-asset-files-uploaded'});
                }
                this.fileUpload.stage = `refresh`;
                await this.$store.dispatch(`mechanicAssets/openUnit`, this.unit.id);
                if (failedFiles.length) {
                    this.fileForm.files = failedFiles;
                    this.errorMessage = `Не загрузились файлы: ${failedFiles.map(file => this.fileDisplayName(file)).join(', ')}`;
                    return;
                }
                this.fileForm = {files: []};
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка загрузки файлов`);
            } finally {
                this.resetFileUpload();
                this.isSubmitting = false;
            }
        },
        async submitPrimaryPhotoForm() {
            const files = Array.isArray(this.primaryPhotoForm.files) ? this.primaryPhotoForm.files : [];
            if (!this.unit || !this.unit.id || this.isSubmitting || !files.length) {
                return;
            }
            this.isSubmitting = true;
            this.errorMessage = null;
            this.primaryPhotoUpload = {
                active: true,
                stage: `upload`,
                currentIndex: 0,
                total: files.length,
                fileName: null,
                fileProgress: 0
            };
            try {
                const failedFiles = await this.uploadAssetFilesSequential(files, {
                    uploadState: this.primaryPhotoUpload,
                    makeFirstPrimary: true
                });
                if (failedFiles.length < files.length) {
                    notifyInstallPromotionSuccess({source: 'mechanic-asset-photos-uploaded'});
                }
                this.primaryPhotoUpload.stage = `refresh`;
                await this.$store.dispatch(`mechanicAssets/openUnit`, this.unit.id);
                if (failedFiles.length) {
                    this.primaryPhotoForm.files = failedFiles;
                    this.errorMessage = `Не загрузились фото: ${failedFiles.map(file => this.fileDisplayName(file)).join(', ')}`;
                    return;
                }
                this.primaryPhotoForm = {files: []};
                this.showPrimaryPhotoModal = false;
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка загрузки заглавного фото`);
            } finally {
                this.resetPrimaryPhotoUpload();
                this.isSubmitting = false;
            }
        },
        async setPrimaryPhoto(file) {
            if (!this.unit || !this.unit.id || !file || !file.id || this.isSubmitting) {
                return;
            }
            this.isSubmitting = true;
            this.errorMessage = null;
            try {
                await this.$store.dispatch(`mechanicAssets/setPrimaryPhoto`, {
                    id: this.unit.id,
                    fileId: file.id,
                    payload: {}
                });
                notifyInstallPromotionSuccess({source: 'mechanic-asset-primary-photo-changed'});
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка выбора заглавного фото`);
            } finally {
                this.isSubmitting = false;
            }
        },
        async setPrimaryPhotoFromModal(file) {
            await this.setPrimaryPhoto(file);
            if (!this.errorMessage) {
                this.showPrimaryPhotoModal = false;
            }
        },
        async deleteFile(file) {
            if (!this.unit || !this.unit.id || !file || !file.id || !this.canWrite || this.isSubmitting) {
                return;
            }
            const confirmed = await this.$bvModal.msgBoxConfirm(`Удалить файл "${this.fileDisplayName(file)}"?`, {
                title: `Удаление файла`,
                okTitle: `Удалить`,
                okVariant: `danger`,
                cancelTitle: `Отмена`,
                centered: true
            });
            if (!confirmed) {
                return;
            }
            this.isSubmitting = true;
            this.errorMessage = null;
            try {
                await this.$store.dispatch(`mechanicAssets/deleteFile`, {
                    id: this.unit.id,
                    fileId: file.id
                });
                notifyInstallPromotionSuccess({source: 'mechanic-asset-file-deleted'});
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка удаления файла`);
            } finally {
                this.isSubmitting = false;
            }
        },
        async submitFuelNormForm() {
            if (!this.unit || !this.unit.id || this.isSubmitting || !this.isFuelNormValid) {
                return;
            }
            this.isSubmitting = true;
            this.errorMessage = null;
            try {
                const payload = this.cleanPayload(this.fuelNormForm);
                delete payload.id;
                if (this.fuelNormForm.id) {
                    await this.$store.dispatch(`mechanicAssets/updateFuelNorm`, {
                        id: this.fuelNormForm.id,
                        payload
                    });
                } else {
                    await this.$store.dispatch(`mechanicAssets/createFuelNorm`, {
                        id: this.unit.id,
                        payload
                    });
                }
                notifyInstallPromotionSuccess({source: 'mechanic-asset-fuel-norm-saved'});
                this.showFuelNormModal = false;
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка сохранения нормы топлива`);
            } finally {
                this.isSubmitting = false;
            }
        },
        async deleteFuelNorm() {
            if (!this.currentFuelNorm || !this.currentFuelNorm.id || this.isSubmitting) {
                return;
            }
            this.isSubmitting = true;
            this.errorMessage = null;
            try {
                await this.$store.dispatch(`mechanicAssets/deleteFuelNorm`, {
                    id: this.currentFuelNorm.id,
                    payload: {}
                });
                notifyInstallPromotionSuccess({source: 'mechanic-asset-fuel-norm-deleted'});
                this.showDeleteFuelNormModal = false;
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка удаления нормы топлива`);
            } finally {
                this.isSubmitting = false;
            }
        },
        async submitMeterForm() {
            if (!this.unit || !this.unit.id || this.isSubmitting || !this.isMeterValid) {
                return;
            }
            this.isSubmitting = true;
            this.errorMessage = null;
            try {
                await this.$store.dispatch(`mechanicAssets/recordMeter`, {
                    id: this.unit.id,
                    payload: this.cleanPayload(this.meterForm)
                });
                notifyInstallPromotionSuccess({source: 'mechanic-asset-meter-recorded'});
                this.showMeterModal = false;
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка записи показания`);
            } finally {
                this.isSubmitting = false;
            }
        },
        async submitRentalForm() {
            if (!this.unit || !this.unit.id || this.isSubmitting || !this.rentalForm.renter_name) {
                return;
            }
            this.isSubmitting = true;
            this.errorMessage = null;
            try {
                await this.$store.dispatch(`mechanicAssets/rentOut`, {
                    id: this.unit.id,
                    payload: this.cleanPayload(this.rentalForm)
                });
                notifyInstallPromotionSuccess({source: 'mechanic-asset-rented'});
                this.showRentModal = false;
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка сдачи в аренду`);
            } finally {
                this.isSubmitting = false;
            }
        },
        async submitReturnRentalForm() {
            if (!this.activeRental || !this.activeRental.id || this.isSubmitting) {
                return;
            }
            this.isSubmitting = true;
            this.errorMessage = null;
            try {
                await this.$store.dispatch(`mechanicAssets/returnRental`, {
                    id: this.activeRental.id,
                    payload: this.cleanPayload(this.returnRentalForm)
                });
                notifyInstallPromotionSuccess({source: 'mechanic-asset-rental-returned'});
                this.showReturnRentalModal = false;
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка возврата из аренды`);
            } finally {
                this.isSubmitting = false;
            }
        },
        async archiveUnit() {
            if (!this.unit || !this.unit.id || this.isSubmitting) {
                return;
            }
            this.isSubmitting = true;
            this.errorMessage = null;
            try {
                await this.$store.dispatch(`mechanicAssets/archiveUnit`, {
                    id: this.unit.id,
                    payload: this.cleanPayload(this.archiveForm)
                });
                notifyInstallPromotionSuccess({source: 'mechanic-asset-archived'});
                this.showArchiveModal = false;
            } catch (err) {
                this.errorMessage = this.errorText(err, `Ошибка архивации позиции`);
            } finally {
                this.isSubmitting = false;
            }
        },
        createUnitForm(unit) {
            const form = emptyUnitForm();
            Object.keys(form).forEach(key => {
                if (key !== `history_comment` && unit && Object.prototype.hasOwnProperty.call(unit, key)) {
                    form[key] = unit[key];
                }
            });
            form.accounting_type = unit.accounting_type || `inventory`;
            form.accounting_company_name = unit.accounting_company_name || unit.owner_name || null;
            form.history_comment = null;
            return form;
        },
        createFuelNormForm(norm) {
            const form = emptyFuelNormForm();
            Object.keys(form).forEach(key => {
                if (Object.prototype.hasOwnProperty.call(norm, key)) {
                    form[key] = norm[key];
                }
            });
            form.effective_from = this.dateInputValue(form.effective_from);
            if (!form.effective_from) {
                form.effective_from = todayInputValue();
            }
            form.effective_to = this.dateInputValue(form.effective_to);
            return form;
        },
        sanitizeUnitPayload(form) {
            return this.cleanPayload({
                catalog_id: form.catalog_id,
                display_name: form.display_name,
                status: form.status,
                location_type: form.location_type,
                warehouse_id: form.warehouse_id,
                building_process_id: form.building_process_id,
                object_group_id: form.object_group_id,
                responsible_user_id: form.responsible_user_id,
                quantity: form.quantity,
                accounting_type: form.accounting_type,
                unit: form.unit,
                technical_condition: form.technical_condition,
                inventory_number: form.inventory_number,
                serial_number: form.serial_number,
                vin: form.vin,
                plate_number: form.plate_number,
                current_meter_type: form.current_meter_type,
                current_meter_value: form.current_meter_value,
                accounting_company_name: form.accounting_company_name,
                owner_kind: form.owner_kind,
                owner_name: form.accounting_company_name,
                comment: form.comment,
                history_comment: form.history_comment
            });
        },
        applyUnitCatalogDefaults() {
            if (!this.unitForm.unit) {
                this.unitForm.unit = `шт`;
            }
        },
        applyUnitAccountingDefaults() {
            if (this.unitFormIsQuantitative) {
                this.unitForm.current_meter_type = null;
                this.unitForm.current_meter_value = null;
                this.unitForm.inventory_number = null;
                this.unitForm.plate_number = null;
                this.unitForm.vin = null;
                this.unitForm.serial_number = null;
                if (this.unitForm.quantity === null || this.unitForm.quantity === undefined) {
                    this.unitForm.quantity = 0;
                }
            } else if (this.unitForm.quantity === null || this.unitForm.quantity === undefined) {
                this.unitForm.quantity = 1;
            }
        },
        normalizeUnitLocation() {
            if (this.unitForm.location_type !== `object_group`) this.unitForm.object_group_id = null;
            if (this.unitUsesWarehouse) {
                this.unitForm.building_process_id = null;
                return;
            }
            if (this.unitUsesObject) {
                this.unitForm.warehouse_id = null;
                return;
            }
            this.unitForm.warehouse_id = null;
            this.unitForm.building_process_id = null;
        },
        normalizeFuelNormWorkFields() {
            if (this.fuelNormUsesWorkGroup) {
                return;
            }
            this.fuelNormForm.work_type_id = null;
            this.fuelNormForm.work_type_name = null;
            this.fuelNormForm.work_unit = null;
        },
        cleanPayload(payload) {
            const result = {};
            Object.keys(payload).forEach(key => {
                const value = payload[key];
                result[key] = value === `` ? null : value;
            });
            return result;
        },
        unitTitle(item) {
            return item.display_name || item.catalog_name || (item.Catalog && item.Catalog.name) || `Позиция #${item.id}`;
        },
        catalogOptionText(row) {
            if (!row) {
                return ``;
            }
            const details = [row.brand, row.model].filter(Boolean).join(` `);
            return details && details !== row.name
                ? `${row.name} / ${details}`
                : row.name;
        },
        quantityText(item) {
            if (!item) {
                return `-`;
            }
            const quantity = item.quantity === null || item.quantity === undefined ? `0` : item.quantity;
            return `${this.formatQuantity(quantity)} ${item.unit || `шт`}`;
        },
        formatQuantity(value) {
            const number = Number(value);
            return Number.isFinite(number)
                ? number.toLocaleString(`ru-RU`, {maximumFractionDigits: 3})
                : value;
        },
        locationName(item) {
            if (item.location_type === `object_group`) return `Группа: ${item.object_group_name || `#${item.object_group_id}`} — без конкретного объекта`;
            return item.warehouse_name || item.object_name || item.source_warehouse_name || item.source_project_name || ``;
        },
        transferPointText(point) {
            if (!point) {
                return `не указано`;
            }
            return point.object_name ||
                point.warehouse_name ||
                point.renter_name ||
                point.location_label ||
                point.location_type ||
                `не указано`;
        },
        transferText(transfer) {
            if (!transfer) {
                return ``;
            }
            return `${this.transferPointText(transfer.from)} -> ${this.transferPointText(transfer.to)}`;
        },
        transferFromText(transfer) {
            return transfer ? this.transferPointText(transfer.from) : `не указано`;
        },
        transferToText(transfer) {
            return transfer ? this.transferPointText(transfer.to) : `не указано`;
        },
        transferRequestId(transfer) {
            return transfer && transfer.request_id ? transfer.request_id : null;
        },
        transferRequestNumber(transfer) {
            return transfer ? (transfer.request_number || transfer.request_id) : ``;
        },
        transferRequestTo(transfer) {
            const requestId = this.transferRequestId(transfer);
            return requestId ? `/requests/${requestId}` : `/requests`;
        },
        ownerCompanyText(item) {
            return (item && (item.accounting_company_name || item.owner_name)) || `-`;
        },
        accountingTypeText(value) {
            return ACCOUNTING_TYPE_LABELS[value] || value || `-`;
        },
        catalogCategoryText(value) {
            return CATALOG_CATEGORY_LABELS[value] || value || `-`;
        },
        fileUrl(file) {
            if (!this.unit || !this.unit.id || !file || !file.id) {
                return `#`;
            }
            const token = this.$store && this.$store.state ? this.$store.state.token : null;
            const url = `/api/mechanic-assets/units/${this.unit.id}/files/${file.id}`;
            return token ? withQueryParams(url, {gjuid: token}) : url;
        },
        previewFileUrl(file, width, height) {
            if (!this.unit || !this.unit.id || !file || !file.id) {
                return `#`;
            }
            const token = this.$store && this.$store.state ? this.$store.state.token : null;
            return withQueryParams(`/api/mechanic-assets/units/${this.unit.id}/files/${file.id}`, {
                thumb: 1,
                w: Number(width) || 128,
                h: Number(height) || 96,
                gjuid: token
            });
        },
        fileDisplayName(file) {
            return getViewerFileDisplayName(file);
        },
        fileFormatLabel(file) {
            return getViewerFileFormatLabel(file);
        },
        fileIconClass(file) {
            return getViewerFileIconClass(file);
        },
        noteFiles(note) {
            return note && Array.isArray(note.Files) ? note.Files : [];
        },
        noteTypeText(value) {
            const option = this.noteTypeOptions.find(item => item.value === value);
            return option ? option.text : (value || `Примечание`);
        },
        isImageFile(file) {
            return isViewerImageFile(file);
        },
        isMainPhoto(file) {
            return Boolean(file && this.mainPhoto && Number(file.id) === Number(this.mainPhoto.id));
        },
        importSourceText(item) {
            const parts = [
                item.facekit_nomenclature_number ? `FaceKIT ${item.facekit_nomenclature_number}` : null,
                item.source_project_name,
                item.source_warehouse_name,
                item.source_responsible_name
            ];
            return parts.filter(Boolean).join(` / `) || `-`;
        },
        meterText(type, value) {
            if (value === null || value === undefined || value === ``) {
                return `-`;
            }
            return `${value} ${this.meterTypeLabel(type)}`.trim();
        },
        meterTypeLabel(value) {
            if (value === `km`) {
                return `км`;
            }
            if (value === `moto_hour`) {
                return `м/ч`;
            }
            return value || ``;
        },
        rentalUserName(user, userId) {
            if (user) {
                return this.userName(user) || `Сотрудник ${userId}`;
            }
            return userId ? `Сотрудник ${userId}` : `-`;
        },
        rentalLocation(rental) {
            if (rental.use_location_text) {
                return rental.use_location_text;
            }
            if (rental.BuildingProcess) {
                return (rental.BuildingProcess.bo && rental.BuildingProcess.bo.name)
                    || rental.BuildingProcess.object_name
                    || `Процесс ${rental.building_process_id}`;
            }
            return rental.building_process_id ? `Процесс ${rental.building_process_id}` : `-`;
        },
        rentalStatusLabel(status) {
            if (status === `active`) {
                return `Активна`;
            }
            if (status === `closed`) {
                return `Закрыта`;
            }
            return status || `-`;
        },
        statusVariant(status) {
            return getMechanicAssetStatusVariant(status);
        },
        historyActionLabel(action) {
            return HISTORY_ACTION_LABELS[action] || action;
        },
        userName(user) {
            return [user.last_name, user.first_name].filter(Boolean).join(` `).trim();
        },
        formatDate(value) {
            if (!value) {
                return ``;
            }
            return new Date(value).toLocaleDateString(`ru-RU`);
        },
        formatDateTime(value) {
            if (!value) {
                return ``;
            }
            return new Date(value).toLocaleString(`ru-RU`);
        },
        dateInputValue(value) {
            if (!value) {
                return null;
            }
            return String(value).slice(0, 10);
        },
        formatHistoryChangeLines(row) {
            const lines = [];
            const event = presentMechanicAssetHistoryRow(row, this.historyPresentationContext);
            if (event.route) {
                lines.push(`Маршрут: ${event.route.from || `не указано`} → ${event.route.to || `не указано`}`);
            } else if (event.summary) {
                lines.push(event.summary);
            }
            event.details.forEach(detail => {
                if (detail.before || detail.after) {
                    lines.push(`${detail.label}: ${detail.before || `не указано`} → ${detail.after || `не указано`}`);
                } else if (detail.value) {
                    lines.push(`${detail.label}: ${detail.value}`);
                }
            });
            return lines;
        },
        errorText(err, fallback) {
            if (err && err.response && err.response.data) {
                if (typeof err.response.data === `string`) {
                    return err.response.data;
                }
                if (err.response.data.msg) {
                    return err.response.data.msg;
                }
            }
            return fallback;
        }
    }
};
</script>

<style scoped>
.mechanic-asset-card {
    min-width: 0;
}

.mechanic-asset-header {
    display: grid;
    grid-template-columns: 180px minmax(0, 1fr);
    gap: 16px;
    align-items: stretch;
    padding: 8px 0 12px;
}

.mechanic-asset-transfer-alert {
    margin: 0 0 10px;
    padding: 8px 10px;
    border-left: 4px solid #17a2b8;
    color: #0c5460;
}

.mechanic-asset-transfer-title {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-bottom: 5px;
}

.mechanic-asset-transfer-badge {
    text-transform: uppercase;
    letter-spacing: 0;
}

.mechanic-asset-transfer-request {
    font-size: 0.9rem;
    font-weight: 600;
    line-height: 1.2;
}

.mechanic-asset-transfer-route {
    display: flex;
    flex-direction: column;
    gap: 5px;
    min-width: 0;
}

.mechanic-asset-transfer-point {
    display: grid;
    grid-template-columns: 58px minmax(0, 1fr);
    align-items: flex-start;
    column-gap: 8px;
    min-width: 0;
    padding: 5px 8px;
    border: 1px solid rgba(23, 162, 184, 0.35);
    border-radius: 4px;
    background: #fff;
}

.mechanic-asset-transfer-point span {
    display: block;
    color: #6c757d;
    font-size: 0.7rem;
    font-weight: 700;
    line-height: 1.25;
    text-transform: uppercase;
}

.mechanic-asset-transfer-point strong {
    display: block;
    min-width: 0;
    overflow-wrap: anywhere;
    white-space: normal;
    color: #083b44;
    font-size: 0.9rem;
    line-height: 1.25;
}

.mechanic-history-changes {
    display: block;
    max-width: 100%;
    line-height: 1.35;
    white-space: normal;
    overflow-wrap: anywhere;
}

.mechanic-history-view-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
}

.mechanic-history-view-toolbar > small {
    color: #6c757d;
    font-size: 12px;
}

.mechanic-history-view-toolbar .btn {
    min-width: 94px;
}

.mechanic-history-line {
    display: block;
    margin-bottom: 2px;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
}

.mechanic-history-table {
    width: 100%;
    table-layout: fixed;
}

.mechanic-history-table td,
.mechanic-history-table th {
    min-width: 0;
    white-space: normal;
    overflow-wrap: anywhere;
    vertical-align: top;
}

.mechanic-history-mobile {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.mechanic-history-card {
    min-width: 0;
    padding: 10px;
    border: 1px solid #dfe5ec;
    border-left: 3px solid #6c757d;
    border-radius: 4px;
    background: #fff;
}

.mechanic-history-card__header {
    display: flex;
    justify-content: space-between;
    gap: 8px;
    min-width: 0;
}

.mechanic-history-card__header strong {
    display: block;
    min-width: 0;
    overflow-wrap: anywhere;
    color: #212529;
    line-height: 1.25;
}

.mechanic-history-card__header small {
    display: block;
    margin-top: 2px;
    color: #6c757d;
    line-height: 1.2;
}

.mechanic-history-card__request {
    flex: 0 0 auto;
    white-space: nowrap;
    font-weight: 700;
}

.mechanic-history-card__meta {
    display: grid;
    gap: 6px;
    margin: 8px 0 0;
}

.mechanic-history-card__meta div {
    display: grid;
    grid-template-columns: 88px minmax(0, 1fr);
    gap: 8px;
    min-width: 0;
}

.mechanic-history-card__meta dt {
    margin: 0;
    color: #6c757d;
    font-size: 11px;
    font-weight: 700;
    line-height: 1.25;
    text-transform: uppercase;
}

.mechanic-history-card__meta dd {
    min-width: 0;
    margin: 0;
    overflow-wrap: anywhere;
    line-height: 1.25;
}

.mechanic-history-card__changes {
    min-width: 0;
    margin-top: 8px;
    padding: 7px 8px;
    border-radius: 4px;
    background: #f8f9fa;
    color: #343a40;
    font-size: 12px;
    line-height: 1.35;
}

.mechanic-asset-photo-box {
    position: relative;
    display: flex;
    min-width: 0;
    min-height: 124px;
    flex-direction: column;
    gap: 6px;
}

.mechanic-asset-primary-photo {
    position: relative;
    width: 100%;
    min-height: 124px;
    height: 124px;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    background: #f8f9fa;
    color: inherit;
}

button.mechanic-asset-primary-photo {
    width: 100%;
    padding: 0;
    cursor: zoom-in;
}

.mechanic-asset-primary-photo:hover {
    color: inherit;
    text-decoration: none;
    border-color: #007bff;
}

.mechanic-asset-primary-photo img {
    width: 100%;
    height: 100%;
    min-height: 124px;
    object-fit: cover;
}

.mechanic-asset-primary-photo--empty {
    flex-direction: column;
    gap: 6px;
    color: #6c757d;
}

.mechanic-asset-primary-photo--empty i {
    font-size: 28px;
}

.mechanic-asset-primary-photo-badge {
    position: absolute;
    left: 8px;
    bottom: 8px;
    padding: 3px 8px;
    border-radius: 4px;
    background: rgba(255, 193, 7, 0.92);
    color: #212529;
    font-size: 12px;
    font-weight: 600;
}

.mechanic-asset-photo-count {
    position: absolute;
    right: 8px;
    bottom: 8px;
    padding: 3px 8px;
    border-radius: 4px;
    background: rgba(33, 37, 41, 0.82);
    color: #fff;
    font-size: 12px;
    font-weight: 700;
}

.mechanic-asset-photo-strip {
    display: flex;
    min-width: 0;
    gap: 6px;
    overflow-x: auto;
    padding: 1px 1px 4px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
}

.mechanic-asset-photo-thumb {
    position: relative;
    flex: 0 0 44px;
    width: 44px;
    height: 36px;
    overflow: hidden;
    padding: 0;
    border: 2px solid #dee2e6;
    border-radius: 4px;
    background: #f8f9fa;
    cursor: pointer;
}

.mechanic-asset-photo-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.mechanic-asset-photo-thumb i {
    position: absolute;
    left: 3px;
    bottom: 3px;
    padding: 2px;
    border-radius: 3px;
    background: rgba(255, 193, 7, 0.92);
    color: #212529;
    font-size: 9px;
}

.mechanic-asset-photo-thumb--active {
    border-color: #007bff;
    box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.2);
}

.mechanic-asset-photo-action {
    position: absolute;
    right: 8px;
    top: 8px;
    z-index: 3;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.18);
}

.mechanic-asset-photo-picker {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 10px;
}

.mechanic-asset-photo-picker-item {
    position: relative;
    height: 112px;
    overflow: hidden;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    background: #f8f9fa;
}

.mechanic-asset-photo-picker-item--active {
    border-color: #ffc107;
    box-shadow: 0 0 0 2px rgba(255, 193, 7, 0.28);
}

.mechanic-asset-photo-picker-item img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.mechanic-asset-photo-picker-badge,
.mechanic-asset-photo-picker-button {
    position: absolute;
    left: 8px;
    bottom: 8px;
}

.mechanic-asset-summary {
    display: grid;
    grid-template-columns: repeat(4, minmax(160px, 1fr));
    gap: 12px;
}

.mechanic-asset-summary span {
    display: block;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.2;
    color: #6c757d;
}

.mechanic-asset-summary strong {
    display: block;
    margin-top: 2px;
    min-width: 0;
    line-height: 1.25;
    overflow-wrap: anywhere;
}

.mechanic-asset-status-badge {
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    margin-top: 3px;
    padding: 4px 8px;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.2;
    white-space: normal;
    overflow-wrap: anywhere;
}

.mechanic-asset-status-badge.badge-primary,
.mechanic-asset-status-badge.badge-success,
.mechanic-asset-status-badge.badge-danger,
.mechanic-asset-status-badge.badge-dark,
.mechanic-asset-status-badge.badge-secondary {
    color: #fff;
}

.mechanic-asset-status-badge.badge-info {
    color: #073642;
}

.mechanic-asset-status-badge.badge-warning {
    color: #3d2b00;
}

.mechanic-asset-status-badge.badge-light {
    border: 1px solid #cfd4da;
    color: #212529;
}

.mechanic-asset-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-bottom: 12px;
}

.mechanic-asset-tab-toolbar {
    display: flex;
    justify-content: flex-end;
    margin: 10px 0;
}

.mechanic-asset-file-upload-block {
    margin: 10px 0 12px;
}

.mechanic-asset-notes {
    display: grid;
    gap: 10px;
    margin-bottom: 12px;
}

.mechanic-asset-note-card {
    padding: 12px;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    background: #fff;
}

.mechanic-asset-note-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 8px;
}

.mechanic-asset-note-meta {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    min-width: 0;
}

.mechanic-asset-note-type {
    border: 1px solid #dee2e6;
    color: #495057;
}

.mechanic-asset-note-text {
    white-space: pre-wrap;
    word-break: break-word;
}

.mechanic-note-file-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(112px, 1fr));
    gap: 8px;
    margin-top: 10px;
}

.mechanic-note-file-preview {
    min-height: 92px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    overflow: hidden;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    background: #f8f9fa;
    color: #343a40;
    text-decoration: none;
}

.mechanic-note-file-preview:hover {
    border-color: #007bff;
    text-decoration: none;
}

.mechanic-note-file-preview img {
    width: 100%;
    height: 86px;
    object-fit: cover;
}

.mechanic-note-file-icon {
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: center;
    justify-content: center;
    min-height: 64px;
    color: #6c757d;
}

.mechanic-note-file-icon i {
    font-size: 24px;
}

.mechanic-note-file-icon small,
.mechanic-file-table-format small {
    font-size: 10px;
    font-weight: 700;
    line-height: 1;
}

.mechanic-note-file-name {
    padding: 5px 6px;
    font-size: 12px;
    line-height: 1.25;
    word-break: break-word;
}

.mechanic-note-upload-status {
    border-left: 4px solid #17a2b8;
}

.mechanic-note-upload-title {
    display: flex;
    align-items: center;
    min-width: 0;
}

.mechanic-note-upload-title span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.mechanic-file-table-link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.mechanic-file-table-preview {
    width: 40px;
    height: 32px;
    flex: 0 0 40px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    background: #f8f9fa;
    color: #6c757d;
}

.mechanic-file-table-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.mechanic-file-table-format {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
}

.mechanic-file-table-format i {
    font-size: 15px;
}

.mechanic-asset-details {
    display: grid;
    grid-template-columns: 180px minmax(0, 1fr);
    gap: 0 16px;
    margin: 12px 0 0;
}

.mechanic-asset-details dt {
    padding: 8px 0;
    color: #6c757d;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.25;
}

.mechanic-asset-details dd {
    margin: 0;
    padding: 8px 0;
    min-width: 0;
    border-bottom: 1px solid #eef0f2;
    color: #212529;
    line-height: 1.35;
    word-break: break-word;
}

.mechanic-asset-current-meter {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin: 12px 0 4px;
}

.mechanic-asset-rental {
    margin-bottom: 12px;
}

.mechanic-asset-card ::v-deep(.vs__dropdown-toggle) {
    min-height: 38px;
    border-color: #ced4da;
}

.mechanic-asset-card ::v-deep(.vs__search),
.mechanic-asset-card ::v-deep(.vs__selected) {
    margin-top: 3px;
}

.mechanic-asset-tabs >>> .nav-tabs {
    gap: 2px;
    padding: 0;
    margin: 10px 0 12px;
    border-bottom: 2px solid #cfd6dd;
    background: transparent;
}

.mechanic-asset-tabs >>> .nav-tabs .nav-link {
    margin-bottom: -2px;
    padding: 0.5rem 0.85rem;
    border: 1px solid transparent;
    border-bottom: 2px solid transparent;
    border-radius: 4px 4px 0 0;
    background: transparent;
    color: #495057;
    font-weight: 600;
    line-height: 1.2;
}

.mechanic-asset-tabs >>> .nav-tabs .nav-link:hover {
    border-color: #e2e6ea #e2e6ea #cfd6dd;
    background: #f8f9fa;
    color: #212529;
}

.mechanic-asset-tabs >>> .nav-tabs .nav-link.active {
    border-color: #cfd6dd #cfd6dd #fff;
    border-top: 3px solid #007bff;
    background: #fff;
    color: #0056b3;
    box-shadow: 0 -1px 0 #007bff;
}

.mechanic-asset-tabs >>> .tab-content {
    padding-top: 2px;
}

@media (max-width: 992px) {
    .mechanic-asset-header {
        grid-template-columns: 150px minmax(0, 1fr);
    }

    .mechanic-asset-summary {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (max-width: 576px) {
    .mechanic-asset-header,
    .mechanic-asset-summary,
    .mechanic-asset-details {
        grid-template-columns: 1fr;
    }

    .mechanic-asset-summary {
        gap: 0;
    }

    .mechanic-asset-summary > div {
        padding: 8px 0;
        border-bottom: 1px solid #eef0f2;
    }

    .mechanic-asset-summary > div:last-child {
        border-bottom: 0;
    }

    .mechanic-asset-details {
        gap: 0;
    }

    .mechanic-asset-details dt {
        padding: 10px 0 2px;
        font-size: 11px;
        line-height: 1.2;
        text-transform: uppercase;
    }

    .mechanic-asset-details dd {
        padding: 0 0 10px;
        font-size: 15px;
        font-weight: 600;
    }

    .mechanic-asset-transfer-alert {
        padding: 0.6rem;
    }

    .mechanic-asset-transfer-point {
        grid-template-columns: 48px minmax(0, 1fr);
        padding: 4px 5px;
    }

    .mechanic-asset-transfer-point strong {
        font-size: 0.82rem;
    }

    .mechanic-asset-primary-photo {
        min-height: 180px;
        height: 180px;
    }

    .mechanic-asset-primary-photo img {
        min-height: 180px;
    }

    .mechanic-asset-photo-box {
        min-height: 180px;
    }

    .mechanic-asset-photo-thumb {
        flex-basis: 62px;
        width: 62px;
        height: 48px;
    }

    .mechanic-asset-tab-toolbar {
        justify-content: stretch;
    }

    .mechanic-asset-tab-toolbar .btn {
        width: 100%;
    }

    .mechanic-asset-note-head {
        display: block;
    }

    .mechanic-asset-note-head small {
        display: block;
        margin-top: 4px;
    }

    .mechanic-note-file-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .mechanic-note-file-preview img {
        height: 112px;
    }

}

@media (max-width: 767.98px) {
    .mechanic-history-view-toolbar {
        align-items: stretch;
        flex-direction: column;
    }

    .mechanic-history-view-toolbar .btn-group,
    .mechanic-history-view-toolbar .btn {
        width: 100%;
    }

    .mechanic-history-view-toolbar > small {
        align-self: flex-end;
    }

    .mechanic-asset-tabs >>> .nav-tabs {
        flex-wrap: wrap;
        overflow: visible;
        white-space: normal;
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
        gap: 4px 2px;
        padding: 0;
    }

    .mechanic-asset-tabs >>> .nav-tabs::-webkit-scrollbar {
        display: none;
    }

    .mechanic-asset-tabs >>> .nav-tabs .nav-item {
        flex: 1 1 auto;
        min-width: 92px;
    }

    .mechanic-asset-tabs >>> .nav-tabs .nav-link {
        width: 100%;
        padding: 0.45rem 0.7rem;
        font-size: 0.875rem;
        border-radius: 4px 4px 0 0;
        text-align: center;
    }

    .mechanic-asset-actions {
        align-items: stretch;
    }

    .mechanic-asset-actions .btn {
        flex: 1 1 calc(50% - 8px);
    }
}
</style>
